"use client";

import { c as _c } from "react/compiler-runtime";
import { CheckCircleIcon } from "@phosphor-icons/react";
import { CheckCircle2, CircleAlert, CircleDashed, Clock3, ListChecks, SquareArrowOutUpRight } from "lucide-react";
import Link from "next/link";
import { getTprDepartmentName } from "@/configuration/groups/tpr/athena";
import { TprCoverageContinueButton } from "@/configuration/groups/tpr/components/coverage-continue-button";
import { TprManualStepContinueButton } from "@/configuration/groups/tpr/components/manual-step-continue-button";
import { TprOpsContinueButton } from "@/configuration/groups/tpr/components/ops-continue-button";
import { TprReceivingAssignmentContinueButton } from "@/configuration/groups/tpr/components/receiving-assignment-continue-button";
import { TprSyncAuthButtons } from "@/configuration/groups/tpr/components/sync-auth-buttons";
import { isTprSpecialCaseInsurance } from "@/configuration/groups/tpr/insurance-special-cases";
import { TPR_MANUAL_REFERRAL_AUTH_DIRECTORY_INSTRUCTION } from "@/configuration/groups/tpr/referral-auth-instructions";
import { describeTprSpecialtyCategories, matchTprDiagnosisSpecialties } from "@/configuration/groups/tpr/workflows/physician-roster";
import { tprReferralAuthNeedsPendingUpload, tprReferralAuthNeedsReview } from "@/configuration/groups/tpr/workflows/referral-auth-status";
import type { StediEligibilityDisplayLabel, TprSyncChecklistTraceEntry, TprSyncChecklistUrgency } from "@/configuration/groups/tpr/workflows/sync-checklist";
import { CopyableContent } from "@/src/components/copyable-content";
import { EhrInstructionsCard } from "@/src/components/ehr-instructions-card";
import { Badge } from "@/src/components/shadcn/ui/badge";
import { AthenaCoverageEvidence } from "@/src/components/tasks/approval-types/athena-coverage-evidence";
import { AthenaReferralCards, AthenaReferralCardsData } from "@/src/components/tasks/athena-referral-cards";
import { ReferralEligibilityCard } from "@/src/components/tasks/referral-eligibility-card";
import { StediEligibilitySummary } from "@/src/components/tasks/stedi-eligibility-summary";
import { SyncIncompleteActions } from "@/src/components/tasks/sync-incomplete-actions";
import { TaskSchedulingRecommendation } from "@/src/components/tasks/task-scheduling-recommendation";
import { VerificationButtons } from "@/src/components/tasks/verification-buttons";
import { ValerieCard } from "@/src/components/valerie-card";
import { useStediEligibilityCheck } from "@/src/lib/data-fetchers/stedi-eligibility-check-fetchers";
import { formatFromUnderscoreToHumanReadable } from "@/src/lib/string";
import { formatDateMmDdYyyy, formatHumanReadableDate } from "@/src/lib/utils/date-and-phone-utils";
import type { ReceivingAssignmentOutput } from "@/src/lib/workflows/steps/referral/resolve-receiving-assignment";
import type { AthenaCoverageReadbackOutput, TprInsuranceRequirementsOutput, TprReferralAuthorizationOutput } from "@/src/lib/workflows/steps/step-outputs";
import { StepKey } from "@/src/lib/workflows/steps/types";
import { getFormattedAddress } from "@/src/models/address";
import type { Appointment } from "@/src/models/appointment";
import { Coverage, getCoverageDisplayName } from "@/src/models/coverage";
import { type DiagnosisItem, Referral } from "@/src/models/documents/referral";
import { FileType } from "@/src/models/file";
import { getPatientDisplayName, getPatientFaxNumber, getPatientName, getPatientPhoneNumber, type Patient } from "@/src/models/patient";
import { getPractitionerNameWithCredentials, Practitioner } from "@/src/models/practitioner";
import { Task, TaskStatus, TaskType } from "@/src/models/task";
import { ApprovalType, type TaskApproval } from "@/src/models/task-approval";
import { type TaskStep, TaskStepStatus } from "@/src/models/task-step";
const NOT_AVAILABLE = "N/A";

/**
 * Lowercased opening segment every TPR disposition note carries — see `getUnifiedReferralInternalNote`
 * in configuration/groups/tpr/workflows/referral-notes.ts. Matched anywhere in the note (not just at
 * the start) because Athena appends internal notes server-side, so ours can end up after an earlier
 * one. Used to tell "Athena already has our note" apart from "Athena has some other note" — e.g.
 * `careTeamStep`'s referring-provider address-mismatch note, which is written earlier and carries no
 * such segment.
 */
const REVIEWED_BY_VALERIE_HEALTH_MARKER = "reviewed by valerie health";

// Plain string literals rather than importing the `TprSyncBucket` enum's runtime values: this is a
// "use client" component, and `sync-checklist.ts` transitively pulls in server-only Athena/GCP client
// code, so any real (non-type-only) import from it breaks the browser bundle. Values must match
// `TprSyncBucket` in configuration/groups/tpr/workflows/sync-checklist.ts exactly.
const TPR_MANUAL_REVIEW_BUCKET = "biltmorecardreferral";

/**
 * Plain-English labels for the 3 real destinations ops actually routes to. `TPR_MANUAL_REVIEW_BUCKET`
 * is deliberately excluded — it's the decision tree's "unable to auto-determine" catch-all, not a 4th
 * destination, so it gets its own "no confident suggestion" message instead of a label here.
 */
const SUGGESTED_BUCKET_LABELS: Record<string, string> = {
  closed: "Closed — upcoming appointment already scheduled",
  sscardio: "SS Cardio — stays with practice",
  biltmorecardiosched: "Biltmore — ready to schedule"
};
const NO_CONFIDENT_BUCKET_SUGGESTION = "No confident suggestion — ops must choose SS Cardio / Biltmore (ready to schedule) / Closed based on the details above.";

/** Renders provider name / NPI as independently copyable fields (no address — that's its own line now). */
function ProviderCopyFields(t0) {
  const $ = _c(9);
  const {
    provider
  } = t0;
  let t1;
  if ($[0] !== provider) {
    t1 = provider && getPractitionerNameWithCredentials(provider) || NOT_AVAILABLE;
    $[0] = provider;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const name = t1;
  const npi = provider?.npiNumber || NOT_AVAILABLE;
  let t2;
  if ($[2] !== name) {
    t2 = <CopyableContent text={name} />;
    $[2] = name;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== npi) {
    t3 = <CopyableContent text={npi} />;
    $[4] = npi;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <>Name: {t2}, NPI: {t3}</>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}
function ResolvedProviderCopyFields(t0) {
  const $ = _c(8);
  const {
    providerLabel,
    providerNpi,
    writeSafe
  } = t0;
  const t1 = providerLabel || NOT_AVAILABLE;
  let t2;
  if ($[0] !== t1) {
    t2 = <CopyableContent text={t1} />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = providerNpi || NOT_AVAILABLE;
  let t4;
  if ($[2] !== t3) {
    t4 = <CopyableContent text={t3} />;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = !writeSafe && " \u2014 verify manually before entry";
  let t6;
  if ($[4] !== t2 || $[5] !== t4 || $[6] !== t5) {
    t6 = <>Name: {t2}, NPI:{" "}{t4}{t5}</>;
    $[4] = t2;
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  return t6;
}
function getCoverageSummary(coverage: Coverage | null | undefined): string | null {
  if (!coverage) return null;
  const display = getCoverageDisplayName({
    coverage,
    includesPlan: true,
    includeSubscriberName: true
  });
  return coverage.subscriberId ? `${display} (Member ID: ${coverage.subscriberId})` : display;
}

/** One line per past appointment for the "Patient Type" sanity-check list, most-recent-first. */
function formatAppointmentHistoryEntry(appointment: Appointment): string {
  const date = appointment.appointmentDatetime ? formatHumanReadableDate(appointment.appointmentDatetime) : "TBD";
  const type = formatFromUnderscoreToHumanReadable(appointment.type) || NOT_AVAILABLE;
  const status = formatFromUnderscoreToHumanReadable(appointment.status || "") || NOT_AVAILABLE;
  return `${date} - ${type} (${status})`;
}
function sortAppointmentsMostRecentFirst(appointments: Appointment[]): Appointment[] {
  return [...appointments].sort((a, b) => {
    const aTime = a.appointmentDatetime ? new Date(a.appointmentDatetime).getTime() : 0;
    const bTime = b.appointmentDatetime ? new Date(b.appointmentDatetime).getTime() : 0;
    return bTime - aTime;
  });
}
type DataSourceEntry = {
  label: string;
  value: string;
  source: "Referral" | "Patient Chart" | "N/A";
  missing: boolean;
};

/**
 * A field only counts as truly missing if it's absent from BOTH the referral's extracted data AND
 * the patient's existing Athena chart — an extraction miss isn't "missing" if the chart already has
 * it. Prefers the referral's value when both are present, since that's what's freshest off the fax.
 */
function resolveDataSourceEntry(label: string, referralValue: string | null, chartValue: string | null): DataSourceEntry {
  if (referralValue) return {
    label,
    value: referralValue,
    source: "Referral",
    missing: false
  };
  if (chartValue) return {
    label,
    value: chartValue,
    source: "Patient Chart",
    missing: false
  };
  return {
    label,
    value: NOT_AVAILABLE,
    source: "N/A",
    missing: true
  };
}
type TimelineStatus = "automated" | "verified" | "verify" | "needs_action" | "failed" | "waiting" | "not_run" | "not_needed";
type TimelineStep = {
  number: number;
  title: string;
  status: TimelineStatus;
  summary: string;
  taskStepKeys: StepKey[];
  content?: React.ReactNode;
  continuation?: {
    instruction: string;
    control: React.ReactNode;
  };
};
export type DecisionMetadataItem = {
  label: string;
  value: React.ReactNode;
};
export type TprChartReferralAuth = {
  referralAuthId: string;
  number: string | null;
  startDate: string | null;
  endDate: string | null;
  expired: boolean | null;
};
export type TprChartRequestLetter = {
  documentEhrId: string;
  documentDate: string | null;
  description: string | null;
};
type TprOpsCrossCheck = {
  patientName: string;
  patientDateOfBirth: string;
  patientPhoneNumber: string;
  patientFaxNumber: string;
  patientEthnicity: string;
  patientRace: string;
  patientLanguage: string;
  patientHomeAddress: string;
  receivingProviderName: string;
  receivingProviderNpi: string;
  referringProviderName: string;
  referringProviderNpi: string;
  referringProviderSpecialty: string;
  referringLocationName: string;
  referringLocationAddress: string;
  emergencyContactName: string;
  emergencyContactPhoneNumber: string;
  emergencyContactRelationship: string;
  referralDate: string;
  primaryPlanLabel: string;
  primarySubscriberId: string;
  /** PIMC / Triwest / MercyCare — special-case payers ops wants visually flagged on Insurance. */
  isSpecialCaseInsurance: boolean;
  diagnosis: string;
  /** Which of TPR's four sub-specialty categories the diagnosis text maps to (General /
   * Interventional / Structural / EP) — the bucket the provider resolver matched providers against. */
  diagnosisCategories: string;
};
export function DecisionMetadata(t0) {
  const $ = _c(4);
  const {
    items
  } = t0;
  let t1;
  if ($[0] !== items) {
    t1 = items.map(_temp);
    $[0] = items;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <dl className="flex flex-wrap gap-2">{t1}</dl>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp(item) {
  return <div key={item.label} className="inline-flex items-center gap-1.5 rounded-md border bg-muted/30 px-2 py-1 text-xs"><dt className="text-muted-foreground">{item.label}</dt><dd className="font-medium text-foreground">{item.value}</dd></div>;
}
type ProviderSelectionEvidence = NonNullable<ReceivingAssignmentOutput["providerSelectionEvidence"]>;
type ProviderSelectionStep = NonNullable<ProviderSelectionEvidence["selectedStep"]>;
function providerSelectionReason(selectedStep: ProviderSelectionEvidence["selectedStep"]): string {
  switch (selectedStep) {
    case "upcoming_appointment":
      return "Selected from the earliest qualifying Biltmore appointment within 60 days.";
    case "past_appointment":
      return "Selected from the most recent qualifying completed Biltmore appointment in the past three years.";
    case "usual_provider":
      return "Legacy selection used the Athena usual provider; verify this assignment against appointment history.";
    case "referral_provider":
      return "Selected because this provider is named on the referral.";
    case "diagnosis_match":
      return "Selected because this provider treats the referral diagnosis at the chosen location.";
    default:
      return "No provider was selected automatically.";
  }
}
function ProviderSelectionChecklist(t0) {
  const $ = _c(36);
  const {
    evidence,
    upcomingAppointmentProviderSummary,
    referralProviderName,
    referralProviderNpi,
    diagnosis,
    diagnosisCategories,
    selectedProviderName,
    selectedLocation
  } = t0;
  let t1;
  if ($[0] !== evidence.appointmentProviders) {
    t1 = evidence.appointmentProviders.some(_temp2);
    $[0] = evidence.appointmentProviders;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const hasQualificationEvidence = t1;
  let t2;
  if ($[2] !== evidence.appointmentProviders || $[3] !== evidence.selectedStep || $[4] !== hasQualificationEvidence || $[5] !== upcomingAppointmentProviderSummary) {
    t2 = period => {
      const providers = evidence.appointmentProviders.flatMap(provider_0 => {
        const count = period === "past" ? provider_0.qualifyingPastAppointmentCount ?? 0 : provider_0.qualifyingUpcomingAppointmentCount ?? 0;
        const locationEhrId = period === "past" ? provider_0.mostRecentPastLocationEhrId : provider_0.earliestUpcomingLocationEhrId;
        const location = locationEhrId ? getTprDepartmentName(locationEhrId) ?? `Athena department ${locationEhrId}` : null;
        return count > 0 ? [`${provider_0.providerName}${provider_0.providerNpi ? ` · ${provider_0.providerNpi}` : ""}${location ? ` · ${location}` : ""} (${count})`] : [];
      });
      if (providers.length > 0) {
        return providers.join(", ");
      }
      if (period === "upcoming" && !hasQualificationEvidence && upcomingAppointmentProviderSummary) {
        return upcomingAppointmentProviderSummary;
      }
      if (!hasQualificationEvidence && (evidence.selectedStep === "usual_provider" || evidence.appointmentProviders.length > 0)) {
        return "Not captured for this legacy task";
      }
      return "None";
    };
    $[2] = evidence.appointmentProviders;
    $[3] = evidence.selectedStep;
    $[4] = hasQualificationEvidence;
    $[5] = upcomingAppointmentProviderSummary;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const providerCountSummary = t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[7] !== diagnosis || $[8] !== diagnosisCategories || $[9] !== evidence.pastAppointmentCount || $[10] !== evidence.selectedStep || $[11] !== evidence.upcomingAppointmentCount || $[12] !== providerCountSummary || $[13] !== referralProviderName || $[14] !== referralProviderNpi || $[15] !== selectedLocation || $[16] !== selectedProviderName) {
    const steps = [{
      key: "upcoming_appointment",
      title: "Check for an upcoming appointment",
      items: [{
        label: "Upcoming within 60 days",
        value: evidence.upcomingAppointmentCount
      }, {
        label: "Qualifying Biltmore providers and locations",
        value: providerCountSummary("upcoming")
      }]
    }, {
      key: "past_appointment",
      title: "Check past appointments at Biltmore locations",
      items: [{
        label: "Completed in past 3 years",
        value: evidence.pastAppointmentCount
      }, {
        label: "Qualifying Biltmore providers and locations",
        value: providerCountSummary("past")
      }]
    }, {
      key: "referral_provider",
      title: "Use the provider named on the referral",
      items: [{
        label: "Provider",
        value: referralProviderName
      }, {
        label: "NPI",
        value: referralProviderNpi
      }]
    }, {
      key: "diagnosis_match",
      title: "Sanity-check the pick or find a replacement",
      items: [{
        label: "Diagnosis",
        value: diagnosis
      }, {
        label: "Diagnosis category",
        value: diagnosisCategories
      }, {
        label: "Location",
        value: selectedLocation
      }, {
        label: "Final provider",
        value: selectedProviderName
      }]
    }];
    t6 = "space-y-2 border-t pt-3";
    if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <summary className="cursor-pointer text-sm font-medium">Find the Provider and Location</summary>;
      $[22] = t7;
    } else {
      t7 = $[22];
    }
    t5 = "mt-2 space-y-2";
    t3 = "space-y-2";
    t4 = steps.map((step, index) => {
      const selected = evidence.selectedStep === step.key;
      return <li key={step.key} className={selected ? "space-y-2 rounded-md border border-success/40 bg-success/10 p-3" : "space-y-2 rounded-md border bg-muted/10 p-3"}><div className="flex items-center justify-between gap-3"><span className="text-sm font-medium">Step {index + 1} — {step.title}</span>{selected ? <Badge variant="outline" className="border-success/40 bg-success/10 text-success">Selected</Badge> : null}</div><DecisionMetadata items={selected ? [...step.items, {
          label: "Picked provider",
          value: selectedProviderName
        }, {
          label: "Reason",
          value: providerSelectionReason(evidence.selectedStep)
        }] : step.items} /></li>;
    });
    $[7] = diagnosis;
    $[8] = diagnosisCategories;
    $[9] = evidence.pastAppointmentCount;
    $[10] = evidence.selectedStep;
    $[11] = evidence.upcomingAppointmentCount;
    $[12] = providerCountSummary;
    $[13] = referralProviderName;
    $[14] = referralProviderNpi;
    $[15] = selectedLocation;
    $[16] = selectedProviderName;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
  } else {
    t3 = $[17];
    t4 = $[18];
    t5 = $[19];
    t6 = $[20];
    t7 = $[21];
  }
  let t8;
  if ($[23] !== t3 || $[24] !== t4) {
    t8 = <ol className={t3}>{t4}</ol>;
    $[23] = t3;
    $[24] = t4;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== evidence.selectedStep) {
    t9 = evidence.selectedStep === "usual_provider" ? <div className="rounded-md border border-warn/40 bg-warn/10 p-3 text-sm text-warn-foreground">This assignment was created with the retired Athena usual-provider rule. Verify the provider and location against qualifying appointment history before following it.</div> : null;
    $[26] = evidence.selectedStep;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== t5 || $[29] !== t8 || $[30] !== t9) {
    t10 = <div className={t5}>{t8}{t9}</div>;
    $[28] = t5;
    $[29] = t8;
    $[30] = t9;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== t10 || $[33] !== t6 || $[34] !== t7) {
    t11 = <details className={t6}>{t7}{t10}</details>;
    $[32] = t10;
    $[33] = t6;
    $[34] = t7;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  return t11;
}
function _temp2(provider) {
  return provider.qualifyingUpcomingAppointmentCount !== undefined || provider.qualifyingPastAppointmentCount !== undefined;
}
const TIMELINE_STATUS = {
  automated: {
    label: "Automated",
    className: "border-success-foreground/30 bg-success-foreground/10 text-success-foreground",
    icon: <CheckCircle2 />
  },
  verified: {
    label: "Verified",
    className: "border-success-foreground/30 bg-success-foreground/10 text-success-foreground",
    icon: <CheckCircle2 />
  },
  verify: {
    label: "Verify",
    className: "border-warn/30 bg-warn/10 text-warn-foreground",
    icon: <CircleAlert />
  },
  needs_action: {
    label: "Needs action",
    className: "border-warn-foreground/30 bg-warn-foreground/10 text-warn-foreground",
    icon: <CircleAlert />
  },
  failed: {
    label: "Failed",
    className: "border-destructive-foreground/30 bg-destructive-foreground/10 text-destructive-foreground",
    icon: <CircleAlert />
  },
  waiting: {
    label: "Waiting",
    className: "border-border bg-muted text-muted-foreground",
    icon: <Clock3 />
  },
  not_run: {
    label: "Not run",
    className: "border-border bg-muted text-muted-foreground",
    icon: <CircleDashed />
  },
  not_needed: {
    label: "Not needed",
    className: "border-border bg-muted text-muted-foreground",
    icon: <CircleDashed />
  }
} satisfies Record<TimelineStatus, {
  label: string;
  className: string;
  icon: React.ReactNode;
}>;
function taskStepStatus(taskSteps: TaskStep[], keys: StepKey[]): TimelineStatus {
  const rows = keys.map(key => taskSteps.find(step => step.stepKey === key)).filter(Boolean);
  if (rows.some(row => row?.status === TaskStepStatus.FAILED)) return "failed";
  if (rows.some(row => row?.status === TaskStepStatus.ASYNC || row?.status === TaskStepStatus.PAUSED)) {
    return "waiting";
  }
  if (keys.every(key => taskSteps.some(step => step.stepKey === key && step.status === TaskStepStatus.COMPLETED))) {
    return "automated";
  }
  return "waiting";
}
function TimelineStepCard(t0) {
  const $ = _c(23);
  const {
    task,
    step,
    failedStepKey
  } = t0;
  const effectiveStatus = failedStepKey ? "failed" : step.status;
  const status = TIMELINE_STATUS[effectiveStatus];
  const isSkipped = effectiveStatus === "not_needed";
  let t1;
  if ($[0] !== failedStepKey || $[1] !== step.continuation || $[2] !== task) {
    t1 = failedStepKey ? {
      instruction: "After completing this step manually, continue with the remaining steps.",
      control: <TprManualStepContinueButton task={task} stepKey={failedStepKey} />
    } : step.continuation;
    $[0] = failedStepKey;
    $[1] = step.continuation;
    $[2] = task;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const continuation = t1;
  const cardVariant = effectiveStatus === "automated" || effectiveStatus === "verified" ? "success" : effectiveStatus === "failed" ? "destructive" : effectiveStatus === "needs_action" || effectiveStatus === "verify" ? "warn" : "outline";
  let t2;
  if ($[4] !== continuation) {
    t2 = continuation ? <div className="space-y-3 border-t pt-4"><p className="text-sm text-muted-foreground">{continuation.instruction}</p><div className="flex justify-center">{continuation.control}</div></div> : null;
    $[4] = continuation;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== step.content || $[7] !== t2) {
    t3 = <>{step.content}{t2}</>;
    $[6] = step.content;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const body = t3;
  const t4 = `${step.number}. ${step.title}`;
  const t5 = effectiveStatus === "automated" ? undefined : status.icon;
  const t6 = effectiveStatus === "not_run" || isSkipped ? "bg-muted/20 text-muted-foreground opacity-40 grayscale" : undefined;
  let t7;
  if ($[9] !== effectiveStatus || $[10] !== status) {
    t7 = effectiveStatus === "automated" ? <span className="text-success-foreground" aria-label="Automated"><CheckCircleIcon weight="fill" className="size-5" /></span> : <Badge variant="outline" className={status.className}>{status.label}</Badge>;
    $[9] = effectiveStatus;
    $[10] = status;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== body || $[13] !== isSkipped || $[14] !== step.summary) {
    t8 = isSkipped ? <details><summary className="cursor-pointer text-sm text-muted-foreground">{step.summary}</summary><div className="mt-3 space-y-3">{body}</div></details> : <><p className="text-sm text-muted-foreground">{step.summary}</p>{body}</>;
    $[12] = body;
    $[13] = isSkipped;
    $[14] = step.summary;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== cardVariant || $[17] !== t4 || $[18] !== t5 || $[19] !== t6 || $[20] !== t7 || $[21] !== t8) {
    t9 = <ValerieCard title={t4} icon={t5} className={t6} variant={cardVariant} actions={t7} contentClassName="space-y-3">{t8}</ValerieCard>;
    $[16] = cardVariant;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  return t9;
}
function TprStediEligibilityValues(t0) {
  const $ = _c(5);
  const {
    referralId
  } = t0;
  const {
    eligibilityCheck
  } = useStediEligibilityCheck(referralId);
  let t1;
  if ($[0] !== eligibilityCheck) {
    t1 = <StediEligibilitySummary check={eligibilityCheck} showHeader={false} />;
    $[0] = eligibilityCheck;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <p className="text-sm text-muted-foreground">Recheck or edit coverage on the Information tab.</p>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1) {
    t3 = <div className="space-y-3">{t1}{t2}</div>;
    $[3] = t1;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
function TprSyncTimeline(t0) {
  const $ = _c(458);
  const {
    task,
    referralId,
    taskSteps,
    pendingApproval,
    receivingAssignmentConfirmed,
    followThroughEligible,
    coverageReadback,
    insuranceRequirements,
    referralAuthorization,
    resolvedReceiving,
    patientLabel,
    patientEhrId,
    documentId,
    bucketLabel,
    internalNoteToEnter,
    urgency,
    actionNote,
    decisionTrace,
    insuranceStatusDisplay,
    hasMissingInformation,
    unresolvedUpcomingProviders,
    upcomingAppointmentProviderSummary,
    upcomingAppointments,
    pastAppointments,
    documentUploadLabels,
    privacyNoticeOnFile,
    isPrimaryCareProvider,
    primaryCareProviderAlreadyPresent,
    patientDisplayId,
    opsCrossCheck,
    chartReferralAuths,
    chartRequestLetters
  } = t0;
  let t1;
  if ($[0] !== taskSteps) {
    t1 = taskSteps.some(_temp3);
    $[0] = taskSteps;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isProviderFirst = t1;
  let t2;
  if ($[2] !== pastAppointments || $[3] !== resolvedReceiving.providerSelectionEvidence || $[4] !== upcomingAppointments) {
    t2 = resolvedReceiving.providerSelectionEvidence ?? {
      selectedStep: null,
      upcomingAppointmentCount: upcomingAppointments.length,
      pastAppointmentCount: pastAppointments.length,
      appointmentProviders: []
    };
    $[2] = pastAppointments;
    $[3] = resolvedReceiving.providerSelectionEvidence;
    $[4] = upcomingAppointments;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const providerSelectionEvidence = t2;
  const workflowStillProcessing = task.status === TaskStatus.PROCESSING;
  let coverageApproval;
  let failedStepCard;
  let opsApproval;
  let steps;
  let t3;
  let waitingForAssociatedReferral;
  if ($[6] !== actionNote || $[7] !== bucketLabel || $[8] !== chartReferralAuths || $[9] !== chartRequestLetters || $[10] !== coverageReadback || $[11] !== decisionTrace || $[12] !== documentId || $[13] !== documentUploadLabels || $[14] !== followThroughEligible || $[15] !== hasMissingInformation || $[16] !== insuranceRequirements || $[17] !== insuranceStatusDisplay || $[18] !== internalNoteToEnter || $[19] !== isPrimaryCareProvider || $[20] !== isProviderFirst || $[21] !== opsCrossCheck.diagnosis || $[22] !== opsCrossCheck.diagnosisCategories || $[23] !== opsCrossCheck.emergencyContactName || $[24] !== opsCrossCheck.emergencyContactPhoneNumber || $[25] !== opsCrossCheck.emergencyContactRelationship || $[26] !== opsCrossCheck.isSpecialCaseInsurance || $[27] !== opsCrossCheck.patientDateOfBirth || $[28] !== opsCrossCheck.patientEthnicity || $[29] !== opsCrossCheck.patientFaxNumber || $[30] !== opsCrossCheck.patientHomeAddress || $[31] !== opsCrossCheck.patientLanguage || $[32] !== opsCrossCheck.patientName || $[33] !== opsCrossCheck.patientPhoneNumber || $[34] !== opsCrossCheck.patientRace || $[35] !== opsCrossCheck.primaryPlanLabel || $[36] !== opsCrossCheck.primarySubscriberId || $[37] !== opsCrossCheck.receivingProviderName || $[38] !== opsCrossCheck.receivingProviderNpi || $[39] !== opsCrossCheck.referralDate || $[40] !== opsCrossCheck.referringLocationAddress || $[41] !== opsCrossCheck.referringProviderName || $[42] !== opsCrossCheck.referringProviderNpi || $[43] !== opsCrossCheck.referringProviderSpecialty || $[44] !== patientDisplayId || $[45] !== patientEhrId || $[46] !== patientLabel || $[47] !== pendingApproval || $[48] !== primaryCareProviderAlreadyPresent || $[49] !== privacyNoticeOnFile || $[50] !== providerSelectionEvidence || $[51] !== receivingAssignmentConfirmed || $[52] !== referralAuthorization || $[53] !== referralId || $[54] !== resolvedReceiving.advisories || $[55] !== resolvedReceiving.flags || $[56] !== resolvedReceiving.locationLabel || $[57] !== resolvedReceiving.providerLabel || $[58] !== resolvedReceiving.providerNpi || $[59] !== resolvedReceiving.providerWriteSafe || $[60] !== resolvedReceiving.routedToSscardio || $[61] !== resolvedReceiving.usualProviderWriteSafe || $[62] !== task || $[63] !== taskSteps || $[64] !== unresolvedUpcomingProviders || $[65] !== upcomingAppointmentProviderSummary || $[66] !== urgency.isUrgent || $[67] !== urgency.reason || $[68] !== urgency.schedulingPriority || $[69] !== workflowStillProcessing) {
    const assignmentStatus = isProviderFirst ? taskStepStatus(taskSteps, [StepKey.RESOLVE_RECEIVING_ASSIGNMENT]) : workflowStillProcessing ? "waiting" : "not_run";
    const assignmentDependentStatus = assignmentStatus === "failed" ? "not_run" : assignmentStatus;
    const receivingAssignmentApproval = pendingApproval?.approvalType === ApprovalType.TPR_RECEIVING_ASSIGNMENT ? pendingApproval : null;
    const assignmentRequiresManualReview = assignmentStatus === "automated" && !resolvedReceiving.providerWriteSafe && receivingAssignmentApproval !== null;
    const usesRetiredUsualProviderRule = providerSelectionEvidence.selectedStep === "usual_provider";
    const assignmentNeedsManualConfirmation = !resolvedReceiving.providerWriteSafe || usesRetiredUsualProviderRule;
    const assignmentWasManuallyConfirmed = assignmentStatus === "automated" && assignmentNeedsManualConfirmation && receivingAssignmentConfirmed;
    const assignmentReviewStatus = assignmentWasManuallyConfirmed ? "verified" : assignmentRequiresManualReview || usesRetiredUsualProviderRule ? "needs_action" : assignmentDependentStatus;
    const gateStatus = taskStepStatus(taskSteps, [StepKey.TPR_CAMELBACK_FOLLOW_THROUGH]);
    const assignmentConfirmationStatus = assignmentWasManuallyConfirmed ? "verified" : assignmentReviewStatus === "needs_action" ? "needs_action" : assignmentStatus === "automated" ? gateStatus : assignmentStatus;
    const confirmedNonCamelbackAssignment = assignmentStatus === "automated" && !followThroughEligible && receivingAssignmentApproval === null;
    const skippedFollowThroughStatus = confirmedNonCamelbackAssignment ? "not_needed" : "not_run";
    const skippedFollowThroughSummary = assignmentRequiresManualReview ? "Not run because the receiving provider and location require manual confirmation." : confirmedNonCamelbackAssignment ? "Not needed for the resolved non-Camelback receiving assignment." : "Not run because the receiving assignment has not completed.";
    coverageApproval = pendingApproval?.approvalType === ApprovalType.ATHENA_COVERAGE ? pendingApproval : null;
    opsApproval = pendingApproval?.approvalType === ApprovalType.TPR_REFERRAL_OPS ? pendingApproval : null;
    const opsKind = opsApproval?.metadata?.kind ?? null;
    let t4;
    if ($[76] !== taskSteps) {
      t4 = taskSteps.some(_temp4);
      $[76] = taskSteps;
      $[77] = t4;
    } else {
      t4 = $[77];
    }
    waitingForAssociatedReferral = t4;
    const coverageReadbackComplete = taskSteps.some(_temp5);
    let t5;
    if ($[78] !== taskSteps) {
      t5 = taskSteps.some(_temp6);
      $[78] = taskSteps;
      $[79] = t5;
    } else {
      t5 = $[79];
    }
    const authRequestUploaded = t5;
    let t6;
    if ($[80] !== taskSteps) {
      t6 = taskSteps.some(_temp7);
      $[80] = taskSteps;
      $[81] = t6;
    } else {
      t6 = $[81];
    }
    const notesComplete = t6;
    let t7;
    if ($[82] !== taskSteps) {
      t7 = taskSteps.some(_temp8);
      $[82] = taskSteps;
      $[83] = t7;
    } else {
      t7 = $[83];
    }
    const missingInfoUploadComplete = t7;
    const verificationActionStepNumber = task.status === TaskStatus.VERIFICATION_NEEDED ? 11 : null;
    let t8;
    if ($[84] !== followThroughEligible || $[85] !== skippedFollowThroughStatus || $[86] !== taskSteps) {
      t8 = keys => followThroughEligible ? taskStepStatus(taskSteps, keys) : skippedFollowThroughStatus;
      $[84] = followThroughEligible;
      $[85] = skippedFollowThroughStatus;
      $[86] = taskSteps;
      $[87] = t8;
    } else {
      t8 = $[87];
    }
    const followThroughStatus = t8;
    const resolveConsolidatedStatus = _temp9;
    let coverageStatus = assignmentRequiresManualReview ? "not_run" : taskStepStatus(taskSteps, [StepKey.CHECK_INSURANCE_ELIGIBILITY, StepKey.CREATE_COVERAGE, StepKey.CONFIRM_ATHENA_COVERAGE, StepKey.READ_ATHENA_COVERAGE]);
    if (coverageApproval) {
      coverageStatus = coverageApproval.metadata?.reviewMode === "verify" ? "verify" : "needs_action";
    } else {
      if (coverageReadbackComplete) {
        coverageStatus = "verified";
      }
    }
    const persistedAthenaEligibilityStatus = followThroughStatus([StepKey.SUBMIT_ATHENA_INSURANCE_ELIGIBILITY]);
    const athenaEligibilityStatus = coverageReadback?.primary.selfPay ? "not_needed" : persistedAthenaEligibilityStatus === "automated" && insuranceStatusDisplay === "Unverified" ? "needs_action" : persistedAthenaEligibilityStatus;
    const networkStatus = coverageReadback?.primary.selfPay || referralAuthorization?.outcome === "not_needed" && referralAuthorization.reason === "ineligible" ? "not_needed" : insuranceRequirements && (insuranceRequirements.clinicNetwork === "manual_review" || insuranceRequirements.providerNetwork === "manual_review" || insuranceRequirements.additionalSchedulingInformationRequired) ? "needs_action" : followThroughStatus([StepKey.RESOLVE_TPR_INSURANCE_REQUIREMENTS]);
    const authorizationStatus = opsKind === "authorization_review" ? "verify" : opsKind === "manual_authorization" ? "needs_action" : tprReferralAuthNeedsReview(referralAuthorization) ? "needs_action" : referralAuthorization?.outcome === "not_needed" ? "not_needed" : referralAuthorization?.outcome === "needs_pending_request" ? "automated" : followThroughStatus([StepKey.TPR_REFERRAL_AUTHORIZATION]);
    let t9;
    if ($[88] !== referralAuthorization) {
      t9 = tprReferralAuthNeedsPendingUpload(referralAuthorization);
      $[88] = referralAuthorization;
      $[89] = t9;
    } else {
      t9 = $[89];
    }
    const referralAuthNeedsPendingUpload = t9;
    const pcpFaxStatus = opsKind === "pcp_referral_fax" ? "needs_action" : insuranceRequirements && !insuranceRequirements.referralMustBeFromPcp ? "not_needed" : followThroughStatus([StepKey.TPR_PCP_REFERRAL_FAX]);
    const authRequestUploadStatus = opsKind === "auth_request_upload" ? "needs_action" : referralAuthNeedsPendingUpload ? authRequestUploaded ? "automated" : followThroughStatus([StepKey.TPR_REFERRAL_AUTH_REQUEST_CONFIRM, StepKey.TPR_REFERRAL_AUTH_REQUEST_UPLOAD]) : "not_needed";
    const missingInfoFaxStatus = !hasMissingInformation ? "not_needed" : missingInfoUploadComplete ? "automated" : followThroughStatus([StepKey.ATHENA_UPLOAD_MISSING_INFO_FAX]);
    const outboundFaxStatus = resolveConsolidatedStatus([authRequestUploadStatus, missingInfoFaxStatus]);
    const closeDocumentStatus = opsKind === "close_document" ? "needs_action" : followThroughStatus([StepKey.TPR_CLOSE_DOCUMENT]);
    let fullRegistrationReviewReasons;
    let t10;
    if ($[90] !== privacyNoticeOnFile || $[91] !== task.files) {
      const workflowNote = task.files.find(_temp10)?.note;
      const t11 = privacyNoticeOnFile === false || workflowNote?.includes("Athena privacy notice could not be set automatically") ? "Set Athena Privacy to YES manually." : null;
      let t12;
      if ($[94] !== t11) {
        t12 = [t11].filter(_temp11);
        $[94] = t11;
        $[95] = t12;
      } else {
        t12 = $[95];
      }
      fullRegistrationReviewReasons = t12;
      t10 = workflowNote?.includes("could not be matched in Athena and was not added to the patient's care team") ? "Add the unmatched referring provider to the Athena care team manually." : null;
      $[90] = privacyNoticeOnFile;
      $[91] = task.files;
      $[92] = fullRegistrationReviewReasons;
      $[93] = t10;
    } else {
      fullRegistrationReviewReasons = $[92];
      t10 = $[93];
    }
    let t11;
    if ($[96] !== t10) {
      t11 = [t10].filter(_temp12);
      $[96] = t10;
      $[97] = t11;
    } else {
      t11 = $[97];
    }
    const demographicsReviewReasons = t11;
    const ensurePatientStatus = taskStepStatus(taskSteps, [StepKey.ENSURE_PATIENT]);
    const careTeamStatus = taskStepStatus(taskSteps, [StepKey.SYNC_PATIENT_APPOINTMENTS, StepKey.UPDATE_CARE_TEAM]);
    const fullRegistrationStatus = ensurePatientStatus === "failed" ? "not_run" : ensurePatientStatus === "waiting" ? "waiting" : fullRegistrationReviewReasons.length > 0 ? "needs_action" : "automated";
    const demographicsStatus = ensurePatientStatus === "failed" ? "not_run" : careTeamStatus !== "automated" ? careTeamStatus : demographicsReviewReasons.length > 0 ? "needs_action" : "automated";
    const dispositionStatus = taskStepStatus(taskSteps, [StepKey.REFERRAL_DISPOSITION]);
    const unevaluatedInsuranceDecision = followThroughEligible ? "Pending" : "Not evaluated";
    const inNetworkDisplay = !insuranceRequirements ? unevaluatedInsuranceDecision : insuranceRequirements.clinicNetwork === "out_of_network" || insuranceRequirements.providerNetwork === "out_of_network" ? "No" : insuranceRequirements.clinicNetwork === "in_network" && insuranceRequirements.providerNetwork === "in_network" ? "Yes" : insuranceRequirements.clinicNetwork === "not_applicable" && insuranceRequirements.providerNetwork === "not_applicable" ? "Not applicable" : "Manual review";
    let t12;
    if ($[98] !== insuranceRequirements || $[99] !== unevaluatedInsuranceDecision) {
      t12 = insuranceRequirements ? formatFromUnderscoreToHumanReadable(insuranceRequirements.referralRequirement) : unevaluatedInsuranceDecision;
      $[98] = insuranceRequirements;
      $[99] = unevaluatedInsuranceDecision;
      $[100] = t12;
    } else {
      t12 = $[100];
    }
    const referralRequirementDisplay = t12;
    let t13;
    if ($[101] !== insuranceRequirements || $[102] !== unevaluatedInsuranceDecision) {
      t13 = insuranceRequirements ? formatFromUnderscoreToHumanReadable(insuranceRequirements.authorizationRequirement) : unevaluatedInsuranceDecision;
      $[101] = insuranceRequirements;
      $[102] = unevaluatedInsuranceDecision;
      $[103] = t13;
    } else {
      t13 = $[103];
    }
    const authorizationRequirementDisplay = t13;
    let t14;
    if ($[104] !== opsCrossCheck.primaryPlanLabel) {
      t14 = {
        label: "Valerie plan",
        value: opsCrossCheck.primaryPlanLabel
      };
      $[104] = opsCrossCheck.primaryPlanLabel;
      $[105] = t14;
    } else {
      t14 = $[105];
    }
    let t15;
    if ($[106] !== opsCrossCheck.primarySubscriberId) {
      t15 = {
        label: "Valerie subscriber ID",
        value: opsCrossCheck.primarySubscriberId
      };
      $[106] = opsCrossCheck.primarySubscriberId;
      $[107] = t15;
    } else {
      t15 = $[107];
    }
    let t16;
    if ($[108] !== inNetworkDisplay) {
      t16 = {
        label: "In network",
        value: inNetworkDisplay
      };
      $[108] = inNetworkDisplay;
      $[109] = t16;
    } else {
      t16 = $[109];
    }
    let t17;
    if ($[110] !== referralRequirementDisplay) {
      t17 = {
        label: "Referral",
        value: referralRequirementDisplay
      };
      $[110] = referralRequirementDisplay;
      $[111] = t17;
    } else {
      t17 = $[111];
    }
    let t18;
    if ($[112] !== authorizationRequirementDisplay) {
      t18 = {
        label: "Authorization",
        value: authorizationRequirementDisplay
      };
      $[112] = authorizationRequirementDisplay;
      $[113] = t18;
    } else {
      t18 = $[113];
    }
    let t19;
    if ($[114] !== t14 || $[115] !== t15 || $[116] !== t16 || $[117] !== t17 || $[118] !== t18) {
      t19 = [t14, t15, t16, t17, t18];
      $[114] = t14;
      $[115] = t15;
      $[116] = t16;
      $[117] = t17;
      $[118] = t18;
      $[119] = t19;
    } else {
      t19 = $[119];
    }
    const insuranceCrossCheckItems = t19;
    const insuranceIntakeDetails = coverageApproval?.metadata ? <div className="space-y-3 rounded-md border p-3 text-sm"><DecisionMetadata items={[...insuranceCrossCheckItems, {
        label: "Expected",
        value: coverageApproval.metadata.expectedCoverageCount
      }, {
        label: "In Athena",
        value: coverageApproval.metadata.currentCoverages.length
      }]} /><AthenaCoverageEvidence metadata={coverageApproval.metadata} /></div> : coverageReadback ? <div className="space-y-3 text-sm"><DecisionMetadata items={[...insuranceCrossCheckItems, {
        label: "In Athena",
        value: coverageReadback.coverages.length
      }, {
        label: "Self pay",
        value: coverageReadback.primary.selfPay ? "Yes" : "No"
      }]} /><div className="space-y-1"><p className="font-medium">Currently in Athena</p>{coverageReadback.coverages.length === 0 ? <p>No coverage found.</p> : coverageReadback.coverages.map(_temp13)}</div></div> : followThroughEligible || assignmentStatus === "automated" ? <DecisionMetadata items={insuranceCrossCheckItems} /> : null;
    const t20 = `${opsCrossCheck.patientName} (DOB: ${opsCrossCheck.patientDateOfBirth})`;
    let t21;
    if ($[120] !== t20) {
      t21 = {
        label: "Patient",
        value: <CopyableContent text={t20} textClassName="text-xs" />
      };
      $[120] = t20;
      $[121] = t21;
    } else {
      t21 = $[121];
    }
    let t22;
    if ($[122] !== patientEhrId) {
      t22 = {
        label: "EHR ID",
        value: <CopyableContent text={patientEhrId} textClassName="text-xs" />
      };
      $[122] = patientEhrId;
      $[123] = t22;
    } else {
      t22 = $[123];
    }
    let t23;
    if ($[124] !== documentId) {
      t23 = {
        label: "Document ID",
        value: <CopyableContent text={documentId} textClassName="text-xs" />
      };
      $[124] = documentId;
      $[125] = t23;
    } else {
      t23 = $[125];
    }
    let t24;
    if ($[126] !== opsCrossCheck.patientPhoneNumber) {
      t24 = {
        label: "Phone",
        value: <CopyableContent text={opsCrossCheck.patientPhoneNumber} textClassName="text-xs" />
      };
      $[126] = opsCrossCheck.patientPhoneNumber;
      $[127] = t24;
    } else {
      t24 = $[127];
    }
    let t25;
    if ($[128] !== opsCrossCheck.patientFaxNumber) {
      t25 = {
        label: "Fax",
        value: <CopyableContent text={opsCrossCheck.patientFaxNumber} textClassName="text-xs" />
      };
      $[128] = opsCrossCheck.patientFaxNumber;
      $[129] = t25;
    } else {
      t25 = $[129];
    }
    let t26;
    if ($[130] !== opsCrossCheck.patientEthnicity) {
      t26 = {
        label: "Ethnicity",
        value: opsCrossCheck.patientEthnicity
      };
      $[130] = opsCrossCheck.patientEthnicity;
      $[131] = t26;
    } else {
      t26 = $[131];
    }
    let t27;
    if ($[132] !== opsCrossCheck.patientRace) {
      t27 = {
        label: "Race",
        value: opsCrossCheck.patientRace
      };
      $[132] = opsCrossCheck.patientRace;
      $[133] = t27;
    } else {
      t27 = $[133];
    }
    let t28;
    if ($[134] !== opsCrossCheck.patientLanguage) {
      t28 = {
        label: "Language",
        value: opsCrossCheck.patientLanguage
      };
      $[134] = opsCrossCheck.patientLanguage;
      $[135] = t28;
    } else {
      t28 = $[135];
    }
    let t29;
    if ($[136] !== t21 || $[137] !== t22 || $[138] !== t23 || $[139] !== t24 || $[140] !== t25 || $[141] !== t26 || $[142] !== t27 || $[143] !== t28) {
      t29 = <DecisionMetadata items={[t21, t22, t23, t24, t25, t26, t27, t28]} />;
      $[136] = t21;
      $[137] = t22;
      $[138] = t23;
      $[139] = t24;
      $[140] = t25;
      $[141] = t26;
      $[142] = t27;
      $[143] = t28;
      $[144] = t29;
    } else {
      t29 = $[144];
    }
    let t30;
    if ($[145] !== patientDisplayId) {
      t30 = patientDisplayId ? <Link href={`/patients/${patientDisplayId}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground">Open patient details<SquareArrowOutUpRight className="size-3.5" /></Link> : null;
      $[145] = patientDisplayId;
      $[146] = t30;
    } else {
      t30 = $[146];
    }
    let t31;
    if ($[147] !== t29 || $[148] !== t30) {
      t31 = <div className="space-y-3">{t29}{t30}</div>;
      $[147] = t29;
      $[148] = t30;
      $[149] = t31;
    } else {
      t31 = $[149];
    }
    let t32;
    if ($[150] !== patientLabel) {
      t32 = {
        label: "Patient type",
        value: patientLabel
      };
      $[150] = patientLabel;
      $[151] = t32;
    } else {
      t32 = $[151];
    }
    const t33 = resolvedReceiving.providerLabel || NOT_AVAILABLE;
    let t34;
    if ($[152] !== t33) {
      t34 = {
        label: "Provider",
        value: <CopyableContent text={t33} textClassName="text-xs" />
      };
      $[152] = t33;
      $[153] = t34;
    } else {
      t34 = $[153];
    }
    const t35 = resolvedReceiving.providerNpi || NOT_AVAILABLE;
    let t36;
    if ($[154] !== t35) {
      t36 = {
        label: "NPI",
        value: <CopyableContent text={t35} textClassName="text-xs" />
      };
      $[154] = t35;
      $[155] = t36;
    } else {
      t36 = $[155];
    }
    const t37 = resolvedReceiving.locationLabel || NOT_AVAILABLE;
    let t38;
    if ($[156] !== t37) {
      t38 = {
        label: "Location",
        value: <CopyableContent text={t37} textClassName="text-xs" />
      };
      $[156] = t37;
      $[157] = t38;
    } else {
      t38 = $[157];
    }
    const t39 = usesRetiredUsualProviderRule ? "Verify appointment history" : assignmentRequiresManualReview ? "Manual review" : "Confirmed";
    let t40;
    if ($[158] !== t39) {
      t40 = {
        label: "Resolution",
        value: t39
      };
      $[158] = t39;
      $[159] = t40;
    } else {
      t40 = $[159];
    }
    let t41;
    if ($[160] !== t32 || $[161] !== t34 || $[162] !== t36 || $[163] !== t38 || $[164] !== t40) {
      t41 = <DecisionMetadata items={[t32, t34, t36, t38, t40]} />;
      $[160] = t32;
      $[161] = t34;
      $[162] = t36;
      $[163] = t38;
      $[164] = t40;
      $[165] = t41;
    } else {
      t41 = $[165];
    }
    const t42 = resolvedReceiving.providerLabel || NOT_AVAILABLE;
    const t43 = resolvedReceiving.locationLabel || NOT_AVAILABLE;
    let t44;
    if ($[166] !== opsCrossCheck.diagnosis || $[167] !== opsCrossCheck.diagnosisCategories || $[168] !== opsCrossCheck.receivingProviderName || $[169] !== opsCrossCheck.receivingProviderNpi || $[170] !== providerSelectionEvidence || $[171] !== t42 || $[172] !== t43 || $[173] !== upcomingAppointmentProviderSummary) {
      t44 = <ProviderSelectionChecklist evidence={providerSelectionEvidence} upcomingAppointmentProviderSummary={upcomingAppointmentProviderSummary} referralProviderName={opsCrossCheck.receivingProviderName} referralProviderNpi={opsCrossCheck.receivingProviderNpi} diagnosis={opsCrossCheck.diagnosis} diagnosisCategories={opsCrossCheck.diagnosisCategories} selectedProviderName={t42} selectedLocation={t43} />;
      $[166] = opsCrossCheck.diagnosis;
      $[167] = opsCrossCheck.diagnosisCategories;
      $[168] = opsCrossCheck.receivingProviderName;
      $[169] = opsCrossCheck.receivingProviderNpi;
      $[170] = providerSelectionEvidence;
      $[171] = t42;
      $[172] = t43;
      $[173] = upcomingAppointmentProviderSummary;
      $[174] = t44;
    } else {
      t44 = $[174];
    }
    let t45;
    if ($[175] !== resolvedReceiving.advisories) {
      t45 = resolvedReceiving.advisories.length > 0 ? <div className="space-y-1 rounded-md border border-warn/30 bg-warn/5 p-3"><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Notes</p><ul className="list-disc space-y-1 pl-5 text-sm text-foreground">{resolvedReceiving.advisories.map(_temp14)}</ul></div> : null;
      $[175] = resolvedReceiving.advisories;
      $[176] = t45;
    } else {
      t45 = $[176];
    }
    const t46 = assignmentConfirmationStatus === "needs_action" && resolvedReceiving.flags.length > 0 ? <><ul className="list-disc space-y-1 pl-5 text-sm text-foreground">{resolvedReceiving.flags.map(_temp15)}</ul>{unresolvedUpcomingProviders.length > 0 ? <div className="flex flex-wrap items-center gap-2 rounded-md border border-warn/30 bg-warn/5 p-3"><span className="text-xs font-medium text-muted-foreground">Upcoming appointment provider</span>{unresolvedUpcomingProviders.map(_temp16)}</div> : null}</> : null;
    let t47;
    if ($[177] !== t41 || $[178] !== t44 || $[179] !== t45 || $[180] !== t46) {
      t47 = <div className="space-y-3">{t41}{t44}{t45}{t46}</div>;
      $[177] = t41;
      $[178] = t44;
      $[179] = t45;
      $[180] = t46;
      $[181] = t47;
    } else {
      t47 = $[181];
    }
    let t48;
    if ($[182] === Symbol.for("react.memo_cache_sentinel")) {
      t48 = <p className="text-sm font-medium">Patient Details</p>;
      $[182] = t48;
    } else {
      t48 = $[182];
    }
    let t49;
    if ($[183] !== opsCrossCheck.patientName) {
      t49 = {
        label: "Patient",
        value: opsCrossCheck.patientName
      };
      $[183] = opsCrossCheck.patientName;
      $[184] = t49;
    } else {
      t49 = $[184];
    }
    let t50;
    if ($[185] !== opsCrossCheck.patientDateOfBirth) {
      t50 = {
        label: "DOB",
        value: opsCrossCheck.patientDateOfBirth
      };
      $[185] = opsCrossCheck.patientDateOfBirth;
      $[186] = t50;
    } else {
      t50 = $[186];
    }
    let t51;
    if ($[187] !== opsCrossCheck.patientPhoneNumber) {
      t51 = {
        label: "Phone",
        value: opsCrossCheck.patientPhoneNumber
      };
      $[187] = opsCrossCheck.patientPhoneNumber;
      $[188] = t51;
    } else {
      t51 = $[188];
    }
    let t52;
    if ($[189] !== opsCrossCheck.patientHomeAddress) {
      t52 = {
        label: "Home address",
        value: opsCrossCheck.patientHomeAddress
      };
      $[189] = opsCrossCheck.patientHomeAddress;
      $[190] = t52;
    } else {
      t52 = $[190];
    }
    let t53;
    if ($[191] !== t49 || $[192] !== t50 || $[193] !== t51 || $[194] !== t52) {
      t53 = <div className="space-y-2 rounded-md border p-3">{t48}<DecisionMetadata items={[t49, t50, t51, t52]} /></div>;
      $[191] = t49;
      $[192] = t50;
      $[193] = t51;
      $[194] = t52;
      $[195] = t53;
    } else {
      t53 = $[195];
    }
    let t54;
    if ($[196] === Symbol.for("react.memo_cache_sentinel")) {
      t54 = <p className="text-sm font-medium">Care Team</p>;
      $[196] = t54;
    } else {
      t54 = $[196];
    }
    let t55;
    if ($[197] === Symbol.for("react.memo_cache_sentinel")) {
      t55 = <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Usual Provider</p>;
      $[197] = t55;
    } else {
      t55 = $[197];
    }
    let t56;
    if ($[198] !== resolvedReceiving.providerLabel || $[199] !== resolvedReceiving.providerNpi || $[200] !== resolvedReceiving.usualProviderWriteSafe) {
      t56 = <div className="space-y-1">{t55}<DecisionMetadata items={resolvedReceiving.usualProviderWriteSafe ? [{
          label: "Provider",
          value: <CopyableContent text={resolvedReceiving.providerLabel || NOT_AVAILABLE} textClassName="text-xs" />
        }, {
          label: "NPI",
          value: <CopyableContent text={resolvedReceiving.providerNpi || NOT_AVAILABLE} textClassName="text-xs" />
        }, {
          label: "Action",
          value: "Update (new patient)"
        }] : [{
          label: "Action",
          value: "Leave unchanged (established patient)"
        }]} /></div>;
      $[198] = resolvedReceiving.providerLabel;
      $[199] = resolvedReceiving.providerNpi;
      $[200] = resolvedReceiving.usualProviderWriteSafe;
      $[201] = t56;
    } else {
      t56 = $[201];
    }
    let t57;
    if ($[202] === Symbol.for("react.memo_cache_sentinel")) {
      t57 = <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Referring Provider</p>;
      $[202] = t57;
    } else {
      t57 = $[202];
    }
    let t58;
    if ($[203] !== opsCrossCheck.referringProviderName) {
      t58 = {
        label: "Name",
        value: <CopyableContent text={opsCrossCheck.referringProviderName} textClassName="text-xs" />
      };
      $[203] = opsCrossCheck.referringProviderName;
      $[204] = t58;
    } else {
      t58 = $[204];
    }
    let t59;
    if ($[205] !== opsCrossCheck.referringProviderNpi) {
      t59 = {
        label: "NPI",
        value: <CopyableContent text={opsCrossCheck.referringProviderNpi} textClassName="text-xs" />
      };
      $[205] = opsCrossCheck.referringProviderNpi;
      $[206] = t59;
    } else {
      t59 = $[206];
    }
    let t60;
    if ($[207] !== opsCrossCheck.referringLocationAddress) {
      t60 = {
        label: "Address",
        value: <CopyableContent text={opsCrossCheck.referringLocationAddress} textClassName="text-xs" />
      };
      $[207] = opsCrossCheck.referringLocationAddress;
      $[208] = t60;
    } else {
      t60 = $[208];
    }
    let t61;
    if ($[209] !== opsCrossCheck.referringProviderSpecialty) {
      t61 = {
        label: "Specialty",
        value: opsCrossCheck.referringProviderSpecialty
      };
      $[209] = opsCrossCheck.referringProviderSpecialty;
      $[210] = t61;
    } else {
      t61 = $[210];
    }
    let t62;
    if ($[211] !== t58 || $[212] !== t59 || $[213] !== t60 || $[214] !== t61) {
      t62 = <div className="space-y-1">{t57}<DecisionMetadata items={[t58, t59, t60, t61]} /></div>;
      $[211] = t58;
      $[212] = t59;
      $[213] = t60;
      $[214] = t61;
      $[215] = t62;
    } else {
      t62 = $[215];
    }
    let t63;
    if ($[216] !== isPrimaryCareProvider || $[217] !== opsCrossCheck.referringProviderName || $[218] !== opsCrossCheck.referringProviderNpi || $[219] !== primaryCareProviderAlreadyPresent) {
      t63 = isPrimaryCareProvider && !primaryCareProviderAlreadyPresent ? <div className="space-y-1"><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">PCP</p><DecisionMetadata items={[{
          label: "Name",
          value: <CopyableContent text={opsCrossCheck.referringProviderName} textClassName="text-xs" />
        }, {
          label: "NPI",
          value: <CopyableContent text={opsCrossCheck.referringProviderNpi} textClassName="text-xs" />
        }]} /></div> : null;
      $[216] = isPrimaryCareProvider;
      $[217] = opsCrossCheck.referringProviderName;
      $[218] = opsCrossCheck.referringProviderNpi;
      $[219] = primaryCareProviderAlreadyPresent;
      $[220] = t63;
    } else {
      t63 = $[220];
    }
    let t64;
    if ($[221] !== t56 || $[222] !== t62 || $[223] !== t63) {
      t64 = <div className="space-y-3 rounded-md border p-3">{t54}{t56}{t62}{t63}</div>;
      $[221] = t56;
      $[222] = t62;
      $[223] = t63;
      $[224] = t64;
    } else {
      t64 = $[224];
    }
    let t65;
    if ($[225] === Symbol.for("react.memo_cache_sentinel")) {
      t65 = <p className="text-sm font-medium">Emergency Contact</p>;
      $[225] = t65;
    } else {
      t65 = $[225];
    }
    let t66;
    if ($[226] !== opsCrossCheck.emergencyContactName) {
      t66 = {
        label: "Name",
        value: <CopyableContent text={opsCrossCheck.emergencyContactName} textClassName="text-xs" />
      };
      $[226] = opsCrossCheck.emergencyContactName;
      $[227] = t66;
    } else {
      t66 = $[227];
    }
    let t67;
    if ($[228] !== opsCrossCheck.emergencyContactRelationship) {
      t67 = {
        label: "Relationship",
        value: <CopyableContent text={opsCrossCheck.emergencyContactRelationship} textClassName="text-xs" />
      };
      $[228] = opsCrossCheck.emergencyContactRelationship;
      $[229] = t67;
    } else {
      t67 = $[229];
    }
    let t68;
    if ($[230] !== opsCrossCheck.emergencyContactPhoneNumber) {
      t68 = {
        label: "Phone",
        value: <CopyableContent text={opsCrossCheck.emergencyContactPhoneNumber} textClassName="text-xs" />
      };
      $[230] = opsCrossCheck.emergencyContactPhoneNumber;
      $[231] = t68;
    } else {
      t68 = $[231];
    }
    let t69;
    if ($[232] !== t66 || $[233] !== t67 || $[234] !== t68) {
      t69 = <div className="space-y-2 rounded-md border p-3">{t65}<DecisionMetadata items={[t66, t67, t68]} /></div>;
      $[232] = t66;
      $[233] = t67;
      $[234] = t68;
      $[235] = t69;
    } else {
      t69 = $[235];
    }
    let t70;
    if ($[236] !== demographicsReviewReasons) {
      t70 = demographicsReviewReasons.length ? <ul className="list-disc space-y-1 pl-5 text-sm text-foreground">{demographicsReviewReasons.map(_temp17)}</ul> : null;
      $[236] = demographicsReviewReasons;
      $[237] = t70;
    } else {
      t70 = $[237];
    }
    let t71;
    if ($[238] !== t53 || $[239] !== t64 || $[240] !== t69 || $[241] !== t70) {
      t71 = <div className="space-y-3">{t53}{t64}{t69}{t70}</div>;
      $[238] = t53;
      $[239] = t64;
      $[240] = t69;
      $[241] = t70;
      $[242] = t71;
    } else {
      t71 = $[242];
    }
    let t72;
    if ($[243] !== opsCrossCheck.primaryPlanLabel) {
      t72 = <CopyableContent text={opsCrossCheck.primaryPlanLabel} textClassName="text-xs" />;
      $[243] = opsCrossCheck.primaryPlanLabel;
      $[244] = t72;
    } else {
      t72 = $[244];
    }
    let t73;
    if ($[245] !== opsCrossCheck.isSpecialCaseInsurance) {
      t73 = opsCrossCheck.isSpecialCaseInsurance ? <Badge variant="outline" className="border-warn/40 bg-warn/10 text-warn-foreground">Special Case Insurance</Badge> : null;
      $[245] = opsCrossCheck.isSpecialCaseInsurance;
      $[246] = t73;
    } else {
      t73 = $[246];
    }
    let t74;
    if ($[247] !== t72 || $[248] !== t73) {
      t74 = {
        label: "Plan (from Valerie)",
        value: <span className="inline-flex items-center gap-1.5">{t72}{t73}</span>
      };
      $[247] = t72;
      $[248] = t73;
      $[249] = t74;
    } else {
      t74 = $[249];
    }
    let t75;
    if ($[250] !== opsCrossCheck.primarySubscriberId) {
      t75 = {
        label: "Subscriber ID (from Valerie)",
        value: <CopyableContent text={opsCrossCheck.primarySubscriberId} textClassName="text-xs" />
      };
      $[250] = opsCrossCheck.primarySubscriberId;
      $[251] = t75;
    } else {
      t75 = $[251];
    }
    const t76 = coverageReadbackComplete ? "Yes" : "No";
    let t77;
    if ($[252] !== t76) {
      t77 = {
        label: "Synced to Athena",
        value: t76
      };
      $[252] = t76;
      $[253] = t77;
    } else {
      t77 = $[253];
    }
    const t78 = coverageReadback?.primary.selfPay ? "N/A \u2014 Self Pay" : inNetworkDisplay;
    let t79;
    if ($[254] !== t78) {
      t79 = {
        label: "In network",
        value: t78
      };
      $[254] = t78;
      $[255] = t79;
    } else {
      t79 = $[255];
    }
    let t80;
    if ($[256] !== t74 || $[257] !== t75 || $[258] !== t77 || $[259] !== t79) {
      t80 = <DecisionMetadata items={[t74, t75, t77, t79]} />;
      $[256] = t74;
      $[257] = t75;
      $[258] = t77;
      $[259] = t79;
      $[260] = t80;
    } else {
      t80 = $[260];
    }
    let t81;
    if ($[261] === Symbol.for("react.memo_cache_sentinel")) {
      t81 = <summary className="cursor-pointer text-muted-foreground">More insurance detail</summary>;
      $[261] = t81;
    } else {
      t81 = $[261];
    }
    let t82;
    if ($[262] === Symbol.for("react.memo_cache_sentinel")) {
      t82 = <p className="text-sm font-medium">Coverage</p>;
      $[262] = t82;
    } else {
      t82 = $[262];
    }
    const t83 = coverageReadback?.primary.selfPay ? "Yes" : "No";
    let t84;
    if ($[263] !== t83) {
      t84 = <DecisionMetadata items={[{
        label: "Self pay",
        value: t83
      }]} />;
      $[263] = t83;
      $[264] = t84;
    } else {
      t84 = $[264];
    }
    let t85;
    if ($[265] !== referralId) {
      t85 = <TprStediEligibilityValues referralId={referralId} />;
      $[265] = referralId;
      $[266] = t85;
    } else {
      t85 = $[266];
    }
    let t86;
    if ($[267] !== insuranceIntakeDetails) {
      t86 = insuranceIntakeDetails ? <details className="text-xs"><summary className="cursor-pointer text-muted-foreground">Show Athena coverage detail</summary><div className="mt-2">{insuranceIntakeDetails}</div></details> : null;
      $[267] = insuranceIntakeDetails;
      $[268] = t86;
    } else {
      t86 = $[268];
    }
    let t87;
    if ($[269] !== t84 || $[270] !== t85 || $[271] !== t86) {
      t87 = <div className="space-y-2 rounded-md border p-3">{t82}{t84}{t85}{t86}</div>;
      $[269] = t84;
      $[270] = t85;
      $[271] = t86;
      $[272] = t87;
    } else {
      t87 = $[272];
    }
    let t88;
    if ($[273] === Symbol.for("react.memo_cache_sentinel")) {
      t88 = <p className="text-sm font-medium">Athena Eligibility</p>;
      $[273] = t88;
    } else {
      t88 = $[273];
    }
    let t89;
    if ($[274] !== followThroughEligible || $[275] !== insuranceStatusDisplay || $[276] !== skippedFollowThroughSummary) {
      t89 = <div className="space-y-2 rounded-md border p-3">{t88}{followThroughEligible ? <DecisionMetadata items={[{
          label: "Athena eligibility",
          value: insuranceStatusDisplay
        }]} /> : <p className="text-sm text-muted-foreground">{skippedFollowThroughSummary}</p>}</div>;
      $[274] = followThroughEligible;
      $[275] = insuranceStatusDisplay;
      $[276] = skippedFollowThroughSummary;
      $[277] = t89;
    } else {
      t89 = $[277];
    }
    let t90;
    if ($[278] !== insuranceRequirements) {
      t90 = insuranceRequirements?.reviewReasons.length ? <div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">Network Review Reasons</p><ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">{insuranceRequirements.reviewReasons.map(_temp18)}</ul></div> : null;
      $[278] = insuranceRequirements;
      $[279] = t90;
    } else {
      t90 = $[279];
    }
    let t91;
    if ($[280] !== t87 || $[281] !== t89 || $[282] !== t90) {
      t91 = <details className="text-xs">{t81}<div className="mt-3 space-y-3">{t87}{t89}{t90}</div></details>;
      $[280] = t87;
      $[281] = t89;
      $[282] = t90;
      $[283] = t91;
    } else {
      t91 = $[283];
    }
    let t92;
    if ($[284] !== t80 || $[285] !== t91) {
      t92 = <div className="space-y-3">{t80}{t91}</div>;
      $[284] = t80;
      $[285] = t91;
      $[286] = t92;
    } else {
      t92 = $[286];
    }
    const t93 = resolveConsolidatedStatus([pcpFaxStatus, authorizationStatus]);
    let t94;
    if ($[287] !== followThroughEligible || $[288] !== opsApproval?.metadata?.instruction || $[289] !== opsKind || $[290] !== referralAuthorization || $[291] !== skippedFollowThroughSummary) {
      t94 = opsKind === "pcp_referral_fax" ? opsApproval?.metadata?.instruction ?? "This plan requires the referral to come from the PCP. Export the chart and send the outbound fax." : opsKind === "authorization_review" ? opsApproval?.metadata?.instruction ?? "Review the Athena referral authorization object, then continue." : opsKind === "manual_authorization" ? opsApproval?.metadata?.instruction ?? TPR_MANUAL_REFERRAL_AUTH_DIRECTORY_INSTRUCTION : referralAuthorization?.outcome === "created" ? `${referralAuthorization.write === "authorization" ? "Authorization" : "Paper referral"} object ${referralAuthorization.alreadyExisted ? "already existed" : "was created"} in Athena${referralAuthorization.pending ? " with number pending" : ""}.` : referralAuthorization?.outcome === "needs_pending_request" ? "No authorization number is on file. An outbound request letter is required next." : tprReferralAuthNeedsReview(referralAuthorization) ? "No Athena object was created because the authorization requirements need manual review." : referralAuthorization?.outcome === "not_needed" ? `No Athena object was needed (${formatFromUnderscoreToHumanReadable(referralAuthorization.reason)}).` : followThroughEligible ? "Waiting for network, authorization, and referral-requirement decisions." : skippedFollowThroughSummary;
      $[287] = followThroughEligible;
      $[288] = opsApproval?.metadata?.instruction;
      $[289] = opsKind;
      $[290] = referralAuthorization;
      $[291] = skippedFollowThroughSummary;
      $[292] = t94;
    } else {
      t94 = $[292];
    }
    let t95;
    if ($[293] === Symbol.for("react.memo_cache_sentinel")) {
      t95 = <p className="text-sm font-medium">PCP Referral Fax</p>;
      $[293] = t95;
    } else {
      t95 = $[293];
    }
    let t96;
    if ($[294] !== followThroughEligible || $[295] !== insuranceRequirements || $[296] !== isPrimaryCareProvider || $[297] !== skippedFollowThroughSummary) {
      t96 = <div className="space-y-2 rounded-md border p-3">{t95}{insuranceRequirements ? <DecisionMetadata items={[{
          label: "PCP referral",
          value: insuranceRequirements.referralMustBeFromPcp ? "Required" : "No"
        }, {
          label: "Referring is PCP",
          value: isPrimaryCareProvider ? "Yes" : "No"
        }]} /> : <p className="text-sm text-muted-foreground">{followThroughEligible ? "Waiting for network/requirements decision." : skippedFollowThroughSummary}</p>}</div>;
      $[294] = followThroughEligible;
      $[295] = insuranceRequirements;
      $[296] = isPrimaryCareProvider;
      $[297] = skippedFollowThroughSummary;
      $[298] = t96;
    } else {
      t96 = $[298];
    }
    let t97;
    if ($[299] === Symbol.for("react.memo_cache_sentinel")) {
      t97 = <p className="text-sm font-medium">Referral Authorization</p>;
      $[299] = t97;
    } else {
      t97 = $[299];
    }
    let t98;
    if ($[300] !== insuranceRequirements || $[301] !== referralAuthorization) {
      t98 = insuranceRequirements ? <DecisionMetadata items={[{
        label: "Authorization",
        value: formatFromUnderscoreToHumanReadable(insuranceRequirements.authorizationRequirement)
      }, {
        label: "Referral",
        value: formatFromUnderscoreToHumanReadable(insuranceRequirements.referralRequirement)
      }, {
        label: "Outcome",
        value: referralAuthorization ? formatFromUnderscoreToHumanReadable(referralAuthorization.outcome) : "Pending"
      }, ...(referralAuthorization?.outcome === "created" ? [{
        label: "Athena object",
        value: <CopyableContent text={referralAuthorization.referralAuthId} textClassName="text-xs" />
      }, {
        label: "Appointment",
        value: referralAuthorization.needsManualAppointmentAttach ? "Attach manually" : referralAuthorization.appointmentAttached ? "Attached" : "None in window"
      }] : [])]} /> : null;
      $[300] = insuranceRequirements;
      $[301] = referralAuthorization;
      $[302] = t98;
    } else {
      t98 = $[302];
    }
    let t99;
    if ($[303] === Symbol.for("react.memo_cache_sentinel")) {
      t99 = <p className="text-sm font-medium">On the Athena chart</p>;
      $[303] = t99;
    } else {
      t99 = $[303];
    }
    let t100;
    if ($[304] === Symbol.for("react.memo_cache_sentinel")) {
      t100 = <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Referral authorizations</p>;
      $[304] = t100;
    } else {
      t100 = $[304];
    }
    let t101;
    if ($[305] !== chartReferralAuths) {
      t101 = <div className="space-y-1">{t100}{chartReferralAuths.length ? <ul className="list-disc space-y-1 pl-5 text-sm">{chartReferralAuths.map(_temp19)}</ul> : <p className="text-sm text-muted-foreground">None on this insurance.</p>}</div>;
      $[305] = chartReferralAuths;
      $[306] = t101;
    } else {
      t101 = $[306];
    }
    let t102;
    if ($[307] === Symbol.for("react.memo_cache_sentinel")) {
      t102 = <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Request letters</p>;
      $[307] = t102;
    } else {
      t102 = $[307];
    }
    let t103;
    if ($[308] !== chartRequestLetters) {
      t103 = <div className="space-y-1">{t102}{chartRequestLetters.length ? <ul className="list-disc space-y-1 pl-5 text-sm">{chartRequestLetters.map(_temp20)}</ul> : <p className="text-sm text-muted-foreground">None uploaded on this task.</p>}</div>;
      $[308] = chartRequestLetters;
      $[309] = t103;
    } else {
      t103 = $[309];
    }
    let t104;
    if ($[310] !== t101 || $[311] !== t103) {
      t104 = <div className="space-y-2">{t99}{t101}{t103}</div>;
      $[310] = t101;
      $[311] = t103;
      $[312] = t104;
    } else {
      t104 = $[312];
    }
    const t105 = Boolean(task.referral && patientEhrId !== NOT_AVAILABLE);
    let t106;
    if ($[313] !== referralAuthNeedsPendingUpload || $[314] !== t105 || $[315] !== task.id) {
      t106 = <TprSyncAuthButtons canUploadLetter={referralAuthNeedsPendingUpload} enabled={t105} taskId={task.id} />;
      $[313] = referralAuthNeedsPendingUpload;
      $[314] = t105;
      $[315] = task.id;
      $[316] = t106;
    } else {
      t106 = $[316];
    }
    let t107;
    if ($[317] === Symbol.for("react.memo_cache_sentinel")) {
      t107 = <summary className="cursor-pointer text-muted-foreground">How to complete this manually</summary>;
      $[317] = t107;
    } else {
      t107 = $[317];
    }
    let t108;
    if ($[318] === Symbol.for("react.memo_cache_sentinel")) {
      t108 = <p className="font-medium text-foreground">Referral Required</p>;
      $[318] = t108;
    } else {
      t108 = $[318];
    }
    let t109;
    if ($[319] === Symbol.for("react.memo_cache_sentinel")) {
      t109 = <li>If a referral is required, confirm the received referral automatically created an object under the Authorization section.</li>;
      $[319] = t109;
    } else {
      t109 = $[319];
    }
    let t110;
    let t111;
    if ($[320] === Symbol.for("react.memo_cache_sentinel")) {
      t110 = <div>{t108}<ul className="list-disc space-y-1 pl-5 text-muted-foreground">{t109}<li>If it did not, manually create the referral object with:<ul className="list-disc space-y-1 pl-5"><li>Insurance / authorization number: always "paper"</li><li>Referral dates — default (if not provided): today through 1 year later</li><li>Number of visits — default (if not provided): 12 visits</li><li>ICD-10 codes</li><li>Referring provider</li><li>Specialty</li><li>Only if listed on the referral doc: receiving provider and department</li></ul></li></ul></div>;
      t111 = <p className="font-medium text-foreground">Referral Authorization Required</p>;
      $[320] = t110;
      $[321] = t111;
    } else {
      t110 = $[320];
      t111 = $[321];
    }
    let t112;
    if ($[322] === Symbol.for("react.memo_cache_sentinel")) {
      t112 = <li>Same workflow as above, but enter the authorization number in the Insurance / authorization number field.</li>;
      $[322] = t112;
    } else {
      t112 = $[322];
    }
    let t113;
    if ($[323] === Symbol.for("react.memo_cache_sentinel")) {
      t113 = <details className="text-xs">{t107}<div className="mt-2 space-y-3">{t110}<div>{t111}<ul className="list-disc space-y-1 pl-5 text-muted-foreground">{t112}<li>Only send a fax if there is no authorization number. When sending an authorization request for a missing auth number:<ul className="list-disc space-y-1 pl-5"><li>Manually export the doc to the referring provider via AthenaFax.</li><li>Add an authorization object with "Pending" in the auth number field.</li><li>In all notes (patient notes, document internal note, action note): "Referral Authorization Requested [Date]"</li></ul></li></ul></div></div></details>;
      $[323] = t113;
    } else {
      t113 = $[323];
    }
    let t114;
    if ($[324] !== t104 || $[325] !== t106 || $[326] !== t98) {
      t114 = <div className="space-y-2 rounded-md border p-3">{t97}{t98}{t104}{t106}{t113}</div>;
      $[324] = t104;
      $[325] = t106;
      $[326] = t98;
      $[327] = t114;
    } else {
      t114 = $[327];
    }
    let t115;
    if ($[328] !== t114 || $[329] !== t96) {
      t115 = <div className="space-y-3">{t96}{t114}</div>;
      $[328] = t114;
      $[329] = t96;
      $[330] = t115;
    } else {
      t115 = $[330];
    }
    let t116;
    if ($[331] !== authRequestUploaded || $[332] !== referralAuthNeedsPendingUpload) {
      t116 = referralAuthNeedsPendingUpload || authRequestUploaded ? <div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">Authorization Request Letter</p><DecisionMetadata items={[{
          label: "Request letter",
          value: authRequestUploaded ? "Uploaded" : "Waiting for ops"
        }]} /><ol className="list-decimal space-y-1 pl-5 text-sm"><li>Export the patient chart in Athena. Do not skip this export.</li><li>Click Upload request letter. The letter is not uploaded automatically.</li><li>Athena then receives a Referral Auth object with number pending.</li></ol></div> : null;
      $[331] = authRequestUploaded;
      $[332] = referralAuthNeedsPendingUpload;
      $[333] = t116;
    } else {
      t116 = $[333];
    }
    let t117;
    if ($[334] !== hasMissingInformation || $[335] !== missingInfoUploadComplete) {
      t117 = hasMissingInformation ? <div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">Missing Patient Phone Packet</p><DecisionMetadata items={[{
          label: "Missing-info packet",
          value: missingInfoUploadComplete ? "Sent to Athena chart" : "Waiting for automation"
        }]} /><p className="text-sm text-muted-foreground">Generated automatically when the patient phone number is missing — no upload needed here. Once it appears in the Athena chart, send it from Athena like any other outbound fax.</p></div> : null;
      $[334] = hasMissingInformation;
      $[335] = missingInfoUploadComplete;
      $[336] = t117;
    } else {
      t117 = $[336];
    }
    const t118 = outboundFaxStatus === "not_needed" ? <p className="text-sm text-muted-foreground">Nothing to send — the referral authorization doesn't need an outbound request, and the patient phone number isn't missing.</p> : null;
    let t119;
    if ($[337] !== t116 || $[338] !== t117 || $[339] !== t118) {
      t119 = <div className="space-y-3">{t116}{t117}{t118}</div>;
      $[337] = t116;
      $[338] = t117;
      $[339] = t118;
      $[340] = t119;
    } else {
      t119 = $[340];
    }
    let t120;
    let t121;
    if ($[341] === Symbol.for("react.memo_cache_sentinel")) {
      t120 = {
        label: "Calls",
        value: "Set to Yes only if blank \u2014 existing value is not overridden"
      };
      t121 = {
        label: "Texts",
        value: "Set to Yes only if blank \u2014 existing value is not overridden"
      };
      $[341] = t120;
      $[342] = t121;
    } else {
      t120 = $[341];
      t121 = $[342];
    }
    const t122 = privacyNoticeOnFile === null ? "Verify selected" : privacyNoticeOnFile ? "Selected" : "Not selected";
    let t123;
    if ($[343] !== t122) {
      t123 = {
        label: "Privacy Notice",
        value: t122
      };
      $[343] = t122;
      $[344] = t123;
    } else {
      t123 = $[344];
    }
    let t124;
    if ($[345] === Symbol.for("react.memo_cache_sentinel")) {
      t124 = {
        label: "How did you hear about us",
        value: "Set to Physician Referral if blank"
      };
      $[345] = t124;
    } else {
      t124 = $[345];
    }
    let t125;
    if ($[346] !== t123) {
      t125 = <DecisionMetadata items={[t120, t121, t123, t124]} />;
      $[346] = t123;
      $[347] = t125;
    } else {
      t125 = $[347];
    }
    let t126;
    if ($[348] !== fullRegistrationReviewReasons) {
      t126 = fullRegistrationReviewReasons.length ? <ul className="list-disc space-y-1 pl-5 text-sm text-warn-foreground">{fullRegistrationReviewReasons.map(_temp21)}</ul> : null;
      $[348] = fullRegistrationReviewReasons;
      $[349] = t126;
    } else {
      t126 = $[349];
    }
    let t127;
    if ($[350] !== t125 || $[351] !== t126) {
      t127 = <div className="space-y-3">{t125}{t126}</div>;
      $[350] = t125;
      $[351] = t126;
      $[352] = t127;
    } else {
      t127 = $[352];
    }
    let t128;
    if ($[353] !== documentId) {
      t128 = <DecisionMetadata items={[{
        label: "Document ID",
        value: <CopyableContent text={documentId} textClassName="text-xs" />
      }]} />;
      $[353] = documentId;
      $[354] = t128;
    } else {
      t128 = $[354];
    }
    let t129;
    if ($[355] === Symbol.for("react.memo_cache_sentinel")) {
      t129 = <p className="text-sm font-medium">Referral Fields — fill out in order</p>;
      $[355] = t129;
    } else {
      t129 = $[355];
    }
    let t130;
    if ($[356] === Symbol.for("react.memo_cache_sentinel")) {
      t130 = <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">1. Referring Provider</p>;
      $[356] = t130;
    } else {
      t130 = $[356];
    }
    let t131;
    if ($[357] !== opsCrossCheck.referringProviderName) {
      t131 = {
        label: "Name",
        value: <CopyableContent text={opsCrossCheck.referringProviderName} textClassName="text-xs" />
      };
      $[357] = opsCrossCheck.referringProviderName;
      $[358] = t131;
    } else {
      t131 = $[358];
    }
    let t132;
    if ($[359] !== opsCrossCheck.referringProviderNpi) {
      t132 = {
        label: "NPI",
        value: <CopyableContent text={opsCrossCheck.referringProviderNpi} textClassName="text-xs" />
      };
      $[359] = opsCrossCheck.referringProviderNpi;
      $[360] = t132;
    } else {
      t132 = $[360];
    }
    let t133;
    if ($[361] !== opsCrossCheck.referringLocationAddress) {
      t133 = {
        label: "Address",
        value: <CopyableContent text={opsCrossCheck.referringLocationAddress} textClassName="text-xs" />
      };
      $[361] = opsCrossCheck.referringLocationAddress;
      $[362] = t133;
    } else {
      t133 = $[362];
    }
    let t134;
    if ($[363] !== t131 || $[364] !== t132 || $[365] !== t133) {
      t134 = <div className="space-y-1">{t130}<DecisionMetadata items={[t131, t132, t133]} /></div>;
      $[363] = t131;
      $[364] = t132;
      $[365] = t133;
      $[366] = t134;
    } else {
      t134 = $[366];
    }
    let t135;
    if ($[367] === Symbol.for("react.memo_cache_sentinel")) {
      t135 = <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">2. New or Established Patient</p>;
      $[367] = t135;
    } else {
      t135 = $[367];
    }
    let t136;
    if ($[368] !== patientLabel) {
      t136 = <div className="space-y-1">{t135}<DecisionMetadata items={[{
          label: "Patient type",
          value: <CopyableContent text={patientLabel} textClassName="text-xs" />
        }]} /></div>;
      $[368] = patientLabel;
      $[369] = t136;
    } else {
      t136 = $[369];
    }
    let t137;
    if ($[370] === Symbol.for("react.memo_cache_sentinel")) {
      t137 = <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">3. Referral Date</p>;
      $[370] = t137;
    } else {
      t137 = $[370];
    }
    let t138;
    if ($[371] !== opsCrossCheck.referralDate) {
      t138 = <div className="space-y-1">{t137}<DecisionMetadata items={[{
          label: "Referral date",
          value: <CopyableContent text={opsCrossCheck.referralDate} textClassName="text-xs" />
        }]} /></div>;
      $[371] = opsCrossCheck.referralDate;
      $[372] = t138;
    } else {
      t138 = $[372];
    }
    let t139;
    if ($[373] === Symbol.for("react.memo_cache_sentinel")) {
      t139 = <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">4. Urgency</p>;
      $[373] = t139;
    } else {
      t139 = $[373];
    }
    const t140 = urgency.isUrgent ? "Yes" : "No";
    let t141;
    if ($[374] !== t140) {
      t141 = {
        label: "Urgent",
        value: t140
      };
      $[374] = t140;
      $[375] = t141;
    } else {
      t141 = $[375];
    }
    let t142;
    if ($[376] !== urgency.reason) {
      t142 = urgency.reason ? [{
        label: "Reason",
        value: urgency.reason
      }] : [];
      $[376] = urgency.reason;
      $[377] = t142;
    } else {
      t142 = $[377];
    }
    const t143 = urgency.schedulingPriority || NOT_AVAILABLE;
    let t144;
    if ($[378] !== t143) {
      t144 = {
        label: "Scheduling priority",
        value: t143
      };
      $[378] = t143;
      $[379] = t144;
    } else {
      t144 = $[379];
    }
    let t145;
    if ($[380] !== t141 || $[381] !== t142 || $[382] !== t144) {
      t145 = <div className="space-y-1">{t139}<DecisionMetadata items={[t141, ...t142, t144]} /></div>;
      $[380] = t141;
      $[381] = t142;
      $[382] = t144;
      $[383] = t145;
    } else {
      t145 = $[383];
    }
    let t146;
    if ($[384] === Symbol.for("react.memo_cache_sentinel")) {
      t146 = <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">5. Internal Note</p>;
      $[384] = t146;
    } else {
      t146 = $[384];
    }
    let t147;
    if ($[385] !== internalNoteToEnter) {
      t147 = <div className="space-y-1">{t146}<DecisionMetadata items={[{
          label: "Internal Note",
          value: <CopyableContent text={internalNoteToEnter} textClassName="text-xs" />
        }]} /></div>;
      $[385] = internalNoteToEnter;
      $[386] = t147;
    } else {
      t147 = $[386];
    }
    let t148;
    if ($[387] !== t134 || $[388] !== t136 || $[389] !== t138 || $[390] !== t145 || $[391] !== t147) {
      t148 = <div className="space-y-3 rounded-md border p-3">{t129}{t134}{t136}{t138}{t145}{t147}</div>;
      $[387] = t134;
      $[388] = t136;
      $[389] = t138;
      $[390] = t145;
      $[391] = t147;
      $[392] = t148;
    } else {
      t148 = $[392];
    }
    let t149;
    if ($[393] !== t128 || $[394] !== t148) {
      t149 = <div className="space-y-3">{t128}{t148}</div>;
      $[393] = t128;
      $[394] = t148;
      $[395] = t149;
    } else {
      t149 = $[395];
    }
    let t150;
    if ($[396] !== documentUploadLabels.length) {
      t150 = <DecisionMetadata items={[{
        label: "Documents",
        value: documentUploadLabels.length
      }]} />;
      $[396] = documentUploadLabels.length;
      $[397] = t150;
    } else {
      t150 = $[397];
    }
    let t151;
    if ($[398] === Symbol.for("react.memo_cache_sentinel")) {
      t151 = <p className="text-sm font-medium">Expected Athena uploads</p>;
      $[398] = t151;
    } else {
      t151 = $[398];
    }
    let t152;
    if ($[399] !== documentUploadLabels) {
      t152 = <div className="space-y-1">{t151}{documentUploadLabels.length ? <ul className="list-disc space-y-1 pl-5 text-sm">{documentUploadLabels.map(_temp22)}</ul> : <p className="text-sm text-muted-foreground">No documents were selected for upload.</p>}</div>;
      $[399] = documentUploadLabels;
      $[400] = t152;
    } else {
      t152 = $[400];
    }
    let t153;
    if ($[401] !== t150 || $[402] !== t152) {
      t153 = <div className="space-y-3">{t150}{t152}</div>;
      $[401] = t150;
      $[402] = t152;
      $[403] = t153;
    } else {
      t153 = $[403];
    }
    let t154;
    if ($[404] === Symbol.for("react.memo_cache_sentinel")) {
      t154 = <p className="text-sm font-medium">Bucket</p>;
      $[404] = t154;
    } else {
      t154 = $[404];
    }
    const t155 = bucketLabel === TPR_MANUAL_REVIEW_BUCKET ? NO_CONFIDENT_BUCKET_SUGGESTION : SUGGESTED_BUCKET_LABELS[bucketLabel] ?? bucketLabel;
    let t156;
    if ($[405] !== t155) {
      t156 = {
        label: "Suggested bucket",
        value: <CopyableContent text={t155} />
      };
      $[405] = t155;
      $[406] = t156;
    } else {
      t156 = $[406];
    }
    const t157 = hasMissingInformation ? "Required" : "No";
    let t158;
    if ($[407] !== t157) {
      t158 = {
        label: "Missing information",
        value: t157
      };
      $[407] = t157;
      $[408] = t158;
    } else {
      t158 = $[408];
    }
    const t159 = authRequestUploaded ? "Uploaded" : "Not required";
    let t160;
    if ($[409] !== t159) {
      t160 = {
        label: "Authorization request",
        value: t159
      };
      $[409] = t159;
      $[410] = t160;
    } else {
      t160 = $[410];
    }
    let t161;
    if ($[411] !== t156 || $[412] !== t158 || $[413] !== t160) {
      t161 = <DecisionMetadata items={[t156, t158, t160]} />;
      $[411] = t156;
      $[412] = t158;
      $[413] = t160;
      $[414] = t161;
    } else {
      t161 = $[414];
    }
    let t162;
    if ($[415] !== decisionTrace) {
      t162 = decisionTrace.length > 0 ? <details className="text-xs"><summary className="cursor-pointer text-muted-foreground">How this was determined</summary><ul className="mt-2 list-disc space-y-1 pl-5 text-muted-foreground">{decisionTrace.map(_temp23)}</ul></details> : null;
      $[415] = decisionTrace;
      $[416] = t162;
    } else {
      t162 = $[416];
    }
    let t163;
    if ($[417] !== t161 || $[418] !== t162) {
      t163 = <div className="space-y-2 rounded-md border p-3">{t154}{t161}{t162}</div>;
      $[417] = t161;
      $[418] = t162;
      $[419] = t163;
    } else {
      t163 = $[419];
    }
    let t164;
    if ($[420] !== notesComplete || $[421] !== resolvedReceiving.routedToSscardio || $[422] !== task.id || $[423] !== task.referral || $[424] !== task.type || $[425] !== workflowStillProcessing) {
      t164 = !resolvedReceiving.routedToSscardio && !workflowStillProcessing && notesComplete && task.type === TaskType.REFERRAL && task.referral && <TaskSchedulingRecommendation taskId={task.id} referral={task.referral} preferAgenticReceivingProvider={true} />;
      $[420] = notesComplete;
      $[421] = resolvedReceiving.routedToSscardio;
      $[422] = task.id;
      $[423] = task.referral;
      $[424] = task.type;
      $[425] = workflowStillProcessing;
      $[426] = t164;
    } else {
      t164 = $[426];
    }
    let t165;
    if ($[427] === Symbol.for("react.memo_cache_sentinel")) {
      t165 = <p className="text-sm font-medium">Action Notes</p>;
      $[427] = t165;
    } else {
      t165 = $[427];
    }
    let t166;
    if ($[428] !== actionNote) {
      t166 = <div className="space-y-2 rounded-md border p-3">{t165}<DecisionMetadata items={[{
          label: "Action Notes",
          value: <CopyableContent text={actionNote} textClassName="text-xs" />
        }]} /></div>;
      $[428] = actionNote;
      $[429] = t166;
    } else {
      t166 = $[429];
    }
    let t167;
    if ($[430] !== t163 || $[431] !== t164 || $[432] !== t166) {
      t167 = <div className="space-y-3">{t163}{t164}{t166}</div>;
      $[430] = t163;
      $[431] = t164;
      $[432] = t166;
      $[433] = t167;
    } else {
      t167 = $[433];
    }
    steps = [{
      number: 1,
      title: "Locate the Document in the Patient Chart",
      status: ensurePatientStatus,
      summary: "Copy and search by the document ID in Referral.",
      taskStepKeys: [StepKey.ENSURE_PATIENT],
      content: t31
    }, {
      number: 2,
      title: "Verify the provider and location",
      status: assignmentConfirmationStatus,
      taskStepKeys: [StepKey.RESOLVE_RECEIVING_ASSIGNMENT, StepKey.TPR_CAMELBACK_FOLLOW_THROUGH],
      summary: usesRetiredUsualProviderRule ? "This legacy assignment used Athena's usual provider and must be checked against qualifying appointment history." : assignmentConfirmationStatus === "needs_action" ? "Provider and location require manual review before downstream referral work." : "Verify we selected the correct receiving provider and location.",
      content: t47,
      continuation: receivingAssignmentApproval ? {
        instruction: "After confirming the provider and location in Athena, continue the workflow.",
        control: <TprReceivingAssignmentContinueButton task={task} />
      } : undefined
    }, {
      number: 3,
      title: "Update demographics and care team",
      status: demographicsStatus,
      taskStepKeys: [StepKey.ENSURE_PATIENT, StepKey.SYNC_PATIENT_APPOINTMENTS, StepKey.UPDATE_CARE_TEAM],
      summary: "Review patient details, care team details, and emergency contact separately in Quickview.",
      content: t71
    }, {
      number: 4,
      title: "Insurance",
      status: resolveConsolidatedStatus([coverageStatus, athenaEligibilityStatus, networkStatus]),
      taskStepKeys: [StepKey.CHECK_INSURANCE_ELIGIBILITY, StepKey.CREATE_COVERAGE, StepKey.CONFIRM_ATHENA_COVERAGE, StepKey.READ_ATHENA_COVERAGE, StepKey.SUBMIT_ATHENA_INSURANCE_ELIGIBILITY, StepKey.SYNC_ATHENA_COVERAGES, StepKey.RESOLVE_TPR_INSURANCE_REQUIREMENTS],
      summary: coverageApproval ? coverageApproval.metadata?.instruction ?? "Confirm the Athena coverage before continuing." : coverageReadback?.primary.selfPay ? "Self Pay \u2014 no network or eligibility checks apply." : insuranceRequirements ? `In network: ${inNetworkDisplay}. Athena eligibility: ${insuranceStatusDisplay}.` : coverageReadbackComplete ? "Coverage confirmed. Waiting on Athena eligibility and network/requirements decisions." : assignmentRequiresManualReview ? "Not run because the receiving provider and location require manual confirmation." : "Waiting for the coverage write, Athena eligibility, and network/requirements decisions.",
      content: t92,
      continuation: coverageApproval ? {
        instruction: "After verifying the coverage sequence in Athena, continue the workflow.",
        control: <TprCoverageContinueButton task={task} />
      } : undefined
    }, {
      number: 5,
      title: "Referral Authorization",
      status: t93,
      taskStepKeys: [StepKey.TPR_PCP_REFERRAL_FAX, StepKey.TPR_REFERRAL_AUTHORIZATION],
      summary: t94,
      content: t115,
      continuation: opsKind === "pcp_referral_fax" ? {
        instruction: opsApproval?.metadata?.instruction ?? "After sending the outbound PCP referral fax from Athena, continue the workflow.",
        control: <TprOpsContinueButton task={task} successTitle="PCP referral fax confirmed" successDescription="The workflow is continuing with authorization." />
      } : opsKind === "authorization_review" ? {
        instruction: opsApproval?.metadata?.instruction ?? "After reviewing the Athena referral authorization object, continue the workflow.",
        control: <TprOpsContinueButton task={task} successTitle="Authorization reviewed" successDescription="The workflow is continuing with the remaining referral steps." />
      } : opsKind === "manual_authorization" ? {
        instruction: opsApproval?.metadata?.instruction ?? TPR_MANUAL_REFERRAL_AUTH_DIRECTORY_INSTRUCTION,
        control: <TprOpsContinueButton task={task} successTitle="Manual authorization recorded" successDescription="The workflow is continuing with the remaining referral steps." />
      } : undefined
    }, {
      number: 6,
      title: "Outbound Faxes",
      status: outboundFaxStatus,
      taskStepKeys: [StepKey.TPR_REFERRAL_AUTH_REQUEST_CONFIRM, StepKey.TPR_REFERRAL_AUTH_REQUEST_UPLOAD, StepKey.ATHENA_UPLOAD_MISSING_INFO_FAX],
      summary: opsKind === "auth_request_upload" ? "Export the patient chart in Athena manually, then upload the request letter. The letter is not uploaded automatically." : outboundFaxStatus === "not_needed" ? "No outbound fax is required for this referral." : "Send the required outbound fax(es) from Athena, then upload where applicable.",
      content: t119,
      continuation: opsKind === "auth_request_upload" ? {
        instruction: "After exporting the chart in Athena, upload the request letter.",
        control: <TprOpsContinueButton task={task} label="Upload request letter" pendingLabel="Uploading..." successTitle="Request letter queued" successDescription="The workflow will write the pending authorization and upload the letter." />
      } : undefined
    }, {
      number: 7,
      title: "Review Full Registration",
      status: fullRegistrationStatus,
      summary: "Open Full Registration and verify communication consent, privacy, and referral source.",
      taskStepKeys: [StepKey.ENSURE_PATIENT],
      content: t127
    }, {
      number: 8,
      title: "Process referral document",
      status: taskStepStatus(taskSteps, [StepKey.REFERRAL_DOCUMENT_METADATA]),
      summary: "Referral metadata is written to Athena in the order ops should verify it.",
      taskStepKeys: [StepKey.REFERRAL_DOCUMENT_METADATA],
      content: t149
    }, {
      number: 9,
      title: "Check document upload",
      status: taskStepStatus(taskSteps, [StepKey.SUPPORTING_SECTIONS_UPLOAD]),
      summary: documentUploadLabels.length ? "Confirm the expected documents were uploaded to Athena." : "No documents were selected for upload.",
      taskStepKeys: [StepKey.SUPPORTING_SECTIONS_UPLOAD],
      content: t153
    }, {
      number: 10,
      title: "Verify in Quickview",
      status: dispositionStatus === "failed" ? "not_run" : notesComplete ? "verify" : "not_run",
      taskStepKeys: [],
      summary: notesComplete ? "Confirm the note above appears correctly on the patient's Quickview tab in Athena." : "Waiting for the final note to be written before this can be verified."
    }, {
      number: 11,
      title: "Bucket Assignment",
      status: resolveConsolidatedStatus([closeDocumentStatus, dispositionStatus === "failed" ? "not_run" : !workflowStillProcessing && notesComplete && (!hasMissingInformation || missingInfoUploadComplete) ? "needs_action" : taskStepStatus(taskSteps, [StepKey.REFERRAL_DISPOSITION, StepKey.ATHENA_UPLOAD_MISSING_INFO_FAX])]),
      taskStepKeys: [StepKey.TPR_CLOSE_DOCUMENT, StepKey.REFERRAL_DISPOSITION, StepKey.SCHEDULING_RECOMMENDATION, StepKey.ATHENA_UPLOAD_MISSING_INFO_FAX],
      summary: opsKind === "close_document" ? opsApproval?.metadata?.instruction ?? "There is an upcoming appointment within 90 days. Close the document in Athena, then continue." : hasMissingInformation && !missingInfoUploadComplete ? "Waiting for the missing-information document to be prepared." : referralAuthNeedsPendingUpload && authRequestUploaded ? "Confirm the authorization-request letter on the chart, route, and complete." : hasMissingInformation ? "Send the missing-information document, route the referral, and complete only after all manual actions are done." : "Confirm the recommended destination bucket \u2014 SS Cardio, Biltmore, or Close \u2014 and complete the task.",
      content: t167,
      continuation: opsKind === "close_document" ? {
        instruction: "After closing the document in Athena, continue the workflow.",
        control: <TprOpsContinueButton task={task} successTitle="Document close confirmed" successDescription="The workflow is continuing with remaining follow-through." />
      } : verificationActionStepNumber === 11 ? {
        instruction: `After completing the manual follow-through in Athena and assigning ${bucketLabel}, continue to complete the task.`,
        control: <VerificationButtons task={task} continueOnly={true} />
      } : undefined
    }];
    let t168;
    if ($[434] !== taskSteps) {
      t168 = taskSteps.find(_temp24);
      $[434] = taskSteps;
      $[435] = t168;
    } else {
      t168 = $[435];
    }
    const failedTaskStep = t168;
    failedStepCard = failedTaskStep ? steps.find(step_6 => step_6.taskStepKeys.some(stepKey => stepKey === failedTaskStep.stepKey)) : undefined;
    t3 = failedTaskStep && failedStepCard ? failedStepCard.taskStepKeys.find(stepKey_0 => stepKey_0 === failedTaskStep.stepKey) ?? null : null;
    $[6] = actionNote;
    $[7] = bucketLabel;
    $[8] = chartReferralAuths;
    $[9] = chartRequestLetters;
    $[10] = coverageReadback;
    $[11] = decisionTrace;
    $[12] = documentId;
    $[13] = documentUploadLabels;
    $[14] = followThroughEligible;
    $[15] = hasMissingInformation;
    $[16] = insuranceRequirements;
    $[17] = insuranceStatusDisplay;
    $[18] = internalNoteToEnter;
    $[19] = isPrimaryCareProvider;
    $[20] = isProviderFirst;
    $[21] = opsCrossCheck.diagnosis;
    $[22] = opsCrossCheck.diagnosisCategories;
    $[23] = opsCrossCheck.emergencyContactName;
    $[24] = opsCrossCheck.emergencyContactPhoneNumber;
    $[25] = opsCrossCheck.emergencyContactRelationship;
    $[26] = opsCrossCheck.isSpecialCaseInsurance;
    $[27] = opsCrossCheck.patientDateOfBirth;
    $[28] = opsCrossCheck.patientEthnicity;
    $[29] = opsCrossCheck.patientFaxNumber;
    $[30] = opsCrossCheck.patientHomeAddress;
    $[31] = opsCrossCheck.patientLanguage;
    $[32] = opsCrossCheck.patientName;
    $[33] = opsCrossCheck.patientPhoneNumber;
    $[34] = opsCrossCheck.patientRace;
    $[35] = opsCrossCheck.primaryPlanLabel;
    $[36] = opsCrossCheck.primarySubscriberId;
    $[37] = opsCrossCheck.receivingProviderName;
    $[38] = opsCrossCheck.receivingProviderNpi;
    $[39] = opsCrossCheck.referralDate;
    $[40] = opsCrossCheck.referringLocationAddress;
    $[41] = opsCrossCheck.referringProviderName;
    $[42] = opsCrossCheck.referringProviderNpi;
    $[43] = opsCrossCheck.referringProviderSpecialty;
    $[44] = patientDisplayId;
    $[45] = patientEhrId;
    $[46] = patientLabel;
    $[47] = pendingApproval;
    $[48] = primaryCareProviderAlreadyPresent;
    $[49] = privacyNoticeOnFile;
    $[50] = providerSelectionEvidence;
    $[51] = receivingAssignmentConfirmed;
    $[52] = referralAuthorization;
    $[53] = referralId;
    $[54] = resolvedReceiving.advisories;
    $[55] = resolvedReceiving.flags;
    $[56] = resolvedReceiving.locationLabel;
    $[57] = resolvedReceiving.providerLabel;
    $[58] = resolvedReceiving.providerNpi;
    $[59] = resolvedReceiving.providerWriteSafe;
    $[60] = resolvedReceiving.routedToSscardio;
    $[61] = resolvedReceiving.usualProviderWriteSafe;
    $[62] = task;
    $[63] = taskSteps;
    $[64] = unresolvedUpcomingProviders;
    $[65] = upcomingAppointmentProviderSummary;
    $[66] = urgency.isUrgent;
    $[67] = urgency.reason;
    $[68] = urgency.schedulingPriority;
    $[69] = workflowStillProcessing;
    $[70] = coverageApproval;
    $[71] = failedStepCard;
    $[72] = opsApproval;
    $[73] = steps;
    $[74] = t3;
    $[75] = waitingForAssociatedReferral;
  } else {
    coverageApproval = $[70];
    failedStepCard = $[71];
    opsApproval = $[72];
    steps = $[73];
    t3 = $[74];
    waitingForAssociatedReferral = $[75];
  }
  const failedStepKey = t3;
  let t4;
  if ($[436] !== coverageApproval || $[437] !== failedStepCard?.number || $[438] !== opsApproval || $[439] !== steps) {
    t4 = coverageApproval ? steps.filter(_temp25) : opsApproval ? steps.filter(_temp26) : steps.filter(step_9 => step_9.number === failedStepCard?.number || step_9.status === "needs_action" || step_9.status === "failed" || step_9.status === "verify");
    $[436] = coverageApproval;
    $[437] = failedStepCard?.number;
    $[438] = opsApproval;
    $[439] = steps;
    $[440] = t4;
  } else {
    t4 = $[440];
  }
  const nextSteps = t4;
  const actionsRemainingVariant = failedStepCard || nextSteps.some(_temp27) ? "destructive" : nextSteps.length || waitingForAssociatedReferral ? "warn" : workflowStillProcessing ? "outline" : "success";
  let t5;
  if ($[441] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ListChecks />;
    $[441] = t5;
  } else {
    t5 = $[441];
  }
  let t6;
  if ($[442] !== failedStepCard?.number || $[443] !== nextSteps || $[444] !== waitingForAssociatedReferral || $[445] !== workflowStillProcessing) {
    t6 = waitingForAssociatedReferral ? <p className="text-sm">Waiting for a matching Referral for this patient. Standalone Referral Auth holds until a Referral in the two-day window finishes syncing.</p> : nextSteps.length ? <ol className="space-y-2 text-sm">{nextSteps.map(step_11 => <li key={step_11.number}><strong>Step {step_11.number}: {step_11.title}</strong><span className="block text-muted-foreground">{step_11.number === failedStepCard?.number ? "Complete this step manually, then continue from its card." : step_11.summary}</span></li>)}</ol> : <p className="text-sm">{workflowStillProcessing ? "Automation is still running. This timeline will update as each durable workflow step completes." : "No manual actions remain."}</p>;
    $[442] = failedStepCard?.number;
    $[443] = nextSteps;
    $[444] = waitingForAssociatedReferral;
    $[445] = workflowStillProcessing;
    $[446] = t6;
  } else {
    t6 = $[446];
  }
  let t7;
  if ($[447] !== actionsRemainingVariant || $[448] !== t6) {
    t7 = <ValerieCard title="Actions remaining" icon={t5} variant={actionsRemainingVariant} contentClassName="space-y-4">{t6}</ValerieCard>;
    $[447] = actionsRemainingVariant;
    $[448] = t6;
    $[449] = t7;
  } else {
    t7 = $[449];
  }
  let t8;
  if ($[450] !== failedStepCard?.number || $[451] !== failedStepKey || $[452] !== steps || $[453] !== task) {
    t8 = steps.map(step_12 => <TimelineStepCard key={step_12.number} task={task} step={step_12} failedStepKey={step_12.number === failedStepCard?.number ? failedStepKey : null} />);
    $[450] = failedStepCard?.number;
    $[451] = failedStepKey;
    $[452] = steps;
    $[453] = task;
    $[454] = t8;
  } else {
    t8 = $[454];
  }
  let t9;
  if ($[455] !== t7 || $[456] !== t8) {
    t9 = <div className="space-y-8 pb-8">{t7}{t8}</div>;
    $[455] = t7;
    $[456] = t8;
    $[457] = t9;
  } else {
    t9 = $[457];
  }
  return t9;
}
function _temp27(step_10) {
  return step_10.status === "failed";
}
function _temp26(step_8) {
  return step_8.continuation;
}
function _temp25(step_7) {
  return step_7.number === 4;
}
function _temp24(step_5) {
  return step_5.status === TaskStepStatus.FAILED;
}
function _temp23(entry, index) {
  return <li key={`${entry.label}-${index}`}>{entry.label} <strong>{entry.outcome}</strong></li>;
}
function _temp22(label) {
  return <li key={label}>{label}</li>;
}
function _temp21(reason_4) {
  return <li key={reason_4}>{reason_4}</li>;
}
function _temp20(letter) {
  return <li key={letter.documentEhrId}><CopyableContent text={letter.documentEhrId} textClassName="text-xs" />{letter.documentDate ? ` · ${letter.documentDate}` : ""}{letter.description ? ` · ${letter.description}` : ""}</li>;
}
function _temp19(authorization) {
  return <li key={authorization.referralAuthId}><CopyableContent text={authorization.referralAuthId} textClassName="text-xs" />{authorization.number ? ` · ${authorization.number}` : ""}{authorization.startDate || authorization.endDate ? ` · ${authorization.startDate ?? "\u2014"}–${authorization.endDate ?? "\u2014"}` : ""}{authorization.expired ? " \xB7 expired" : ""}</li>;
}
function _temp18(reason_3) {
  return <li key={reason_3}>{reason_3}</li>;
}
function _temp17(reason_2) {
  return <li key={reason_2}>{reason_2}</li>;
}
function _temp16(provider) {
  return <Badge key={provider.providerEhrId ?? provider.label} variant="outline">{provider.label}{provider.providerEhrId ? ` · Athena ID ${provider.providerEhrId}` : ""}</Badge>;
}
function _temp15(reason_1) {
  return <li key={reason_1}>{reason_1}</li>;
}
function _temp14(advisory) {
  return <li key={advisory}>{advisory}</li>;
}
function _temp13(coverage) {
  return <p key={coverage.insuranceId}><strong>{coverage.casePolicy ? "Case policy" : coverage.sequence === 1 ? "Primary" : coverage.sequence === 2 ? "Secondary" : `Sequence ${coverage.sequence ?? "N/A"}`}:</strong>{" "}{coverage.planName ?? "Unknown plan"} · Package <CopyableContent text={coverage.insurancePackageId} />{coverage.subscriberId ? <>{" "}· Subscriber <CopyableContent text={coverage.subscriberId} /></> : null}</p>;
}
function _temp12(reason_0) {
  return reason_0 !== null;
}
function _temp11(reason) {
  return reason !== null;
}
function _temp10(file) {
  return file.type === FileType.INBOUND;
}
function _temp9(statuses) {
  if (statuses.includes("failed")) {
    return "failed";
  }
  if (statuses.includes("needs_action")) {
    return "needs_action";
  }
  if (statuses.includes("verify")) {
    return "verify";
  }
  if (statuses.includes("waiting")) {
    return "waiting";
  }
  if (statuses.includes("verified")) {
    return "verified";
  }
  if (statuses.includes("automated")) {
    return "automated";
  }
  if (statuses.includes("not_run")) {
    return "not_run";
  }
  return "not_needed";
}
function _temp8(step_4) {
  return step_4.stepKey === StepKey.ATHENA_UPLOAD_MISSING_INFO_FAX && step_4.status === TaskStepStatus.COMPLETED;
}
function _temp7(step_3) {
  return step_3.stepKey === StepKey.REFERRAL_DISPOSITION && step_3.status === TaskStepStatus.COMPLETED;
}
function _temp6(step_2) {
  return step_2.stepKey === StepKey.TPR_REFERRAL_AUTH_REQUEST_UPLOAD && step_2.status === TaskStepStatus.COMPLETED && step_2.output?.uploaded === true;
}
function _temp5(step_1) {
  return step_1.stepKey === StepKey.READ_ATHENA_COVERAGE && step_1.status === TaskStepStatus.COMPLETED;
}
function _temp4(step_0) {
  return step_0.stepKey === StepKey.TPR_WAIT_FOR_ASSOCIATED_REFERRAL && step_0.status === TaskStepStatus.PAUSED;
}
function _temp3(step) {
  return step.stepKey === StepKey.RESOLVE_RECEIVING_ASSIGNMENT;
}
interface TprReferralInstructionsProps {
  view: "information" | "steps";
  referral: Referral;
  task: Task;
  athenaData: AthenaReferralCardsData | null;
  patientEhrId: string;
  /** "New Patient Referral" / "Established Patient Referral" / "N/A" — see getAthenaReferralDocumentLabel. */
  patientLabel: string;
  upcomingAppointments: Appointment[];
  /** Past booked appointments (lookback window matching the new/established determination) for the
   * "Patient Type" sanity-check list — display-only, not the source of the New/Established label itself. */
  pastAppointments: Appointment[];
  documentId: string;
  isPrimaryCareProvider: boolean;
  /** Whether the referring provider is already on the Athena care team as PRIMARY_CARE_PROVIDER —
   * suppresses the redundant "PCP" line in Step 3's care team card. See resolveTprCareTeamPolicy. */
  primaryCareProviderAlreadyPresent: boolean;
  /** One of TprSyncBucket's four values: "closed" | "sscardio" | "biltmorecardreferral" | "biltmorecardiosched" */
  bucketLabel: string;
  actionNote: string;
  /** Athena document priority (Urgent vs Normal) and why — shown in Step 8 before the internal note. */
  urgency: TprSyncChecklistUrgency;
  /** The note the disposition step will write, always carrying the
   * "Reviewed By Valerie Health (date) - <provider> - ..." prefix. Step 8 shows this rather than the
   * Athena read-back, which is still empty at that point in the workflow. */
  expectedInternalNote: string;
  /** Each decision-tree check that ran, in order, with its outcome — see resolveTprSyncChecklist. */
  decisionTrace: TprSyncChecklistTraceEntry[];
  /** Non-stopping context notes from the decision tree (insurance-sync issues, urgency reason, the
   * detailed "ops must manually verify..." explanation behind a short bucket-assignment label, etc). */
  flags: string[];
  /** "Eligible" | "Ineligible" | "Unverified" — same Athena-then-Stedi result the decision tree reads. */
  eligibilityDisplay: StediEligibilityDisplayLabel;
  documentUploadLabels: string[];
  privacyNoticeOnFile: boolean | null;
  taskSteps: TaskStep[];
  pendingApproval: TaskApproval | null;
  receivingAssignmentConfirmed: boolean;
  followThroughEligible: boolean;
  coverageReadback: AthenaCoverageReadbackOutput | null;
  insuranceRequirements: TprInsuranceRequirementsOutput | null;
  referralAuthorization: TprReferralAuthorizationOutput | null;
  unresolvedUpcomingProviders: Array<{
    label: string;
    providerEhrId: string | null;
  }>;
  upcomingAppointmentProviderSummary: string | null;
  /** Persisted provider-first assignment, or the legacy display fallback for older tasks. */
  resolvedReceiving: {
    providerLabel: string | null;
    providerNpi: string | null;
    providerWriteSafe: boolean;
    usualProviderWriteSafe: boolean;
    locationLabel: string | null;
    trace: string[];
    flags: string[];
    /** Non-blocking reviewer notices (e.g. the Terrace cardiology/neurology check) — always shown,
     * unlike `flags`, which only renders once the assignment already needs manual review. */
    advisories: string[];
    routedToSscardio: boolean;
    providerSelectionEvidence?: ReceivingAssignmentOutput["providerSelectionEvidence"];
  };
  chartReferralAuths: TprChartReferralAuth[];
  chartRequestLetters: TprChartRequestLetter[];
}
export function TprReferralInstructions(t0) {
  const $ = _c(416);
  const {
    view,
    referral,
    task,
    athenaData,
    patientEhrId,
    patientLabel,
    upcomingAppointments,
    pastAppointments,
    documentId,
    isPrimaryCareProvider,
    primaryCareProviderAlreadyPresent,
    bucketLabel,
    actionNote,
    urgency,
    expectedInternalNote,
    decisionTrace,
    flags,
    eligibilityDisplay,
    documentUploadLabels,
    privacyNoticeOnFile,
    taskSteps,
    pendingApproval,
    receivingAssignmentConfirmed,
    followThroughEligible,
    coverageReadback,
    insuranceRequirements,
    referralAuthorization,
    unresolvedUpcomingProviders,
    upcomingAppointmentProviderSummary,
    resolvedReceiving,
    chartReferralAuths,
    chartRequestLetters
  } = t0;
  let t1;
  if ($[0] !== referral.referringLocation?.address) {
    t1 = getFormattedAddress(referral.referringLocation?.address) || NOT_AVAILABLE;
    $[0] = referral.referringLocation?.address;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const referringLocationAddress = t1;
  const referringProviderSpecialty = referral.referringProvider?.specialty || NOT_AVAILABLE;
  let t2;
  if ($[2] !== referral.referringProvider) {
    t2 = referral.referringProvider ? getPractitionerNameWithCredentials(referral.referringProvider) : NOT_AVAILABLE;
    $[2] = referral.referringProvider;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const referringProviderName = t2;
  let t3;
  if ($[4] !== referral.receivingProvider) {
    t3 = referral.receivingProvider ? getPractitionerNameWithCredentials(referral.receivingProvider) : NOT_AVAILABLE;
    $[4] = referral.receivingProvider;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const receivingProviderName = t3;
  const patient = referral.patient;
  const emergencyContactName = patient?.emergencyContactName || NOT_AVAILABLE;
  const emergencyContactPhoneNumber = patient?.emergencyContactPhoneNumber || NOT_AVAILABLE;
  const emergencyContactRelationship = patient?.emergencyContactRelationshipToPatient || NOT_AVAILABLE;
  let t4;
  if ($[6] !== referral.primaryCoverage) {
    t4 = getCoverageSummary(referral.primaryCoverage);
    $[6] = referral.primaryCoverage;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const primaryCoverageSummary = t4;
  let t5;
  if ($[8] !== referral.secondaryCoverage) {
    t5 = getCoverageSummary(referral.secondaryCoverage);
    $[8] = referral.secondaryCoverage;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const secondaryCoverageSummary = t5;
  let t6;
  if ($[10] !== referral.dateOfReferral) {
    t6 = formatDateMmDdYyyy(referral.dateOfReferral) || NOT_AVAILABLE;
    $[10] = referral.dateOfReferral;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const dateOfReferral = t6;
  const schedulingPriority = referral.schedulingPriority || NOT_AVAILABLE;
  const athenaInternalNote = athenaData?.document?.internalNote || NOT_AVAILABLE;
  const internalNoteToEnter = athenaInternalNote.toLowerCase().includes(REVIEWED_BY_VALERIE_HEALTH_MARKER) ? athenaInternalNote : expectedInternalNote;
  let T0;
  let dataSourceEntries;
  let hasMissingInformation;
  let missingFields;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  let t24;
  let t25;
  let t7;
  let t8;
  let t9;
  if ($[12] !== actionNote || $[13] !== athenaData?.coverages || $[14] !== athenaData?.patientDetails || $[15] !== bucketLabel || $[16] !== chartReferralAuths || $[17] !== chartRequestLetters || $[18] !== coverageReadback || $[19] !== dateOfReferral || $[20] !== decisionTrace || $[21] !== documentId || $[22] !== documentUploadLabels || $[23] !== eligibilityDisplay || $[24] !== emergencyContactName || $[25] !== emergencyContactPhoneNumber || $[26] !== emergencyContactRelationship || $[27] !== followThroughEligible || $[28] !== insuranceRequirements || $[29] !== internalNoteToEnter || $[30] !== isPrimaryCareProvider || $[31] !== pastAppointments || $[32] !== patient || $[33] !== patientEhrId || $[34] !== patientLabel || $[35] !== pendingApproval || $[36] !== primaryCareProviderAlreadyPresent || $[37] !== primaryCoverageSummary || $[38] !== privacyNoticeOnFile || $[39] !== receivingAssignmentConfirmed || $[40] !== receivingProviderName || $[41] !== referral || $[42] !== referralAuthorization || $[43] !== referringLocationAddress || $[44] !== referringProviderName || $[45] !== referringProviderSpecialty || $[46] !== resolvedReceiving || $[47] !== secondaryCoverageSummary || $[48] !== task || $[49] !== taskSteps || $[50] !== unresolvedUpcomingProviders || $[51] !== upcomingAppointmentProviderSummary || $[52] !== upcomingAppointments || $[53] !== urgency || $[54] !== view) {
    t25 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const sortedPastAppointments = sortAppointmentsMostRecentFirst(pastAppointments);
      const syncFailed = task.status === TaskStatus.FAILED;
      const insuranceStatusDisplay = coverageReadback?.primary.selfPay ? "Self Pay" : referral.primaryCoverage ? eligibilityDisplay : "Missing Insurance";
      const chartPatient = athenaData?.patientDetails ?? null;
      let t26;
      if ($[78] !== patient) {
        t26 = patient && getPatientPhoneNumber(patient)?.trim() || null;
        $[78] = patient;
        $[79] = t26;
      } else {
        t26 = $[79];
      }
      const referralPhone = t26;
      let t27;
      if ($[80] !== chartPatient) {
        t27 = chartPatient && getPatientPhoneNumber(chartPatient)?.trim() || null;
        $[80] = chartPatient;
        $[81] = t27;
      } else {
        t27 = $[81];
      }
      const chartPhone = t27;
      let chartAddressValue;
      let phoneDataSourceEntry;
      let referralAddressValue;
      let t28;
      let t29;
      let t30;
      if ($[82] !== athenaData?.coverages || $[83] !== chartPatient || $[84] !== chartPhone || $[85] !== patient || $[86] !== referral.primaryCoverage || $[87] !== referralPhone) {
        const referralCoverageValue = getCoverageSummary(referral.primaryCoverage);
        const chartPrimaryCoverage = [...(athenaData?.coverages ?? [])].sort(_temp28)[0];
        const chartCoverageValue = getCoverageSummary(chartPrimaryCoverage ?? null);
        referralAddressValue = patient?.homeAddress ? getFormattedAddress(patient.homeAddress) : null;
        chartAddressValue = chartPatient?.homeAddress ? getFormattedAddress(chartPatient.homeAddress) : null;
        let t31;
        if ($[94] !== chartPhone || $[95] !== referralPhone) {
          t31 = resolveDataSourceEntry("Phone Number", referralPhone, chartPhone);
          $[94] = chartPhone;
          $[95] = referralPhone;
          $[96] = t31;
        } else {
          t31 = $[96];
        }
        phoneDataSourceEntry = t31;
        t28 = phoneDataSourceEntry;
        t29 = resolveDataSourceEntry("Insurance", referralCoverageValue, chartCoverageValue);
        t30 = resolveDataSourceEntry("Home Address", referralAddressValue, chartAddressValue);
        $[82] = athenaData?.coverages;
        $[83] = chartPatient;
        $[84] = chartPhone;
        $[85] = patient;
        $[86] = referral.primaryCoverage;
        $[87] = referralPhone;
        $[88] = chartAddressValue;
        $[89] = phoneDataSourceEntry;
        $[90] = referralAddressValue;
        $[91] = t28;
        $[92] = t29;
        $[93] = t30;
      } else {
        chartAddressValue = $[88];
        phoneDataSourceEntry = $[89];
        referralAddressValue = $[90];
        t28 = $[91];
        t29 = $[92];
        t30 = $[93];
      }
      let t31;
      if ($[97] !== t28 || $[98] !== t29 || $[99] !== t30) {
        t31 = [t28, t29, t30];
        $[97] = t28;
        $[98] = t29;
        $[99] = t30;
        $[100] = t31;
      } else {
        t31 = $[100];
      }
      dataSourceEntries = t31;
      const isCamelbackReferral = !resolvedReceiving.routedToSscardio;
      let t32;
      if ($[101] !== isCamelbackReferral || $[102] !== phoneDataSourceEntry) {
        t32 = isCamelbackReferral && phoneDataSourceEntry.missing ? [phoneDataSourceEntry] : [];
        $[101] = isCamelbackReferral;
        $[102] = phoneDataSourceEntry;
        $[103] = t32;
      } else {
        t32 = $[103];
      }
      missingFields = t32;
      hasMissingInformation = missingFields.length > 0;
      let t33;
      if ($[104] !== patient) {
        t33 = getPatientDisplayName(patient);
        $[104] = patient;
        $[105] = t33;
      } else {
        t33 = $[105];
      }
      const patientDisplayName = t33;
      const receivingLocationName = referral.receivingLocation?.name || NOT_AVAILABLE;
      let t34;
      if ($[106] !== referral.diagnosisList || $[107] !== referral.reasonForReferral) {
        t34 = [referral.reasonForReferral, ...((referral.diagnosisList ?? []) as DiagnosisItem[]).map(_temp29)].filter(_temp30).filter(_temp31).join(", ") || NOT_AVAILABLE;
        $[106] = referral.diagnosisList;
        $[107] = referral.reasonForReferral;
        $[108] = t34;
      } else {
        t34 = $[108];
      }
      const diagnosis = t34;
      let t35;
      if ($[109] !== referral) {
        t35 = describeTprSpecialtyCategories(matchTprDiagnosisSpecialties(referral)) || "None matched";
        $[109] = referral;
        $[110] = t35;
      } else {
        t35 = $[110];
      }
      const diagnosisCategories = t35;
      let t36;
      if ($[111] !== patient) {
        t36 = patient ? getPatientName(patient) || NOT_AVAILABLE : NOT_AVAILABLE;
        $[111] = patient;
        $[112] = t36;
      } else {
        t36 = $[112];
      }
      let t37;
      if ($[113] !== patient?.dateOfBirth) {
        t37 = formatDateMmDdYyyy(patient?.dateOfBirth) || NOT_AVAILABLE;
        $[113] = patient?.dateOfBirth;
        $[114] = t37;
      } else {
        t37 = $[114];
      }
      const t38 = chartPhone ?? referralPhone ?? NOT_AVAILABLE;
      let t39;
      if ($[115] !== patient) {
        t39 = patient && getPatientFaxNumber(patient) || NOT_AVAILABLE;
        $[115] = patient;
        $[116] = t39;
      } else {
        t39 = $[116];
      }
      const t40 = patient?.ethnicity || NOT_AVAILABLE;
      const t41 = patient?.race || NOT_AVAILABLE;
      const t42 = patient?.language || NOT_AVAILABLE;
      const t43 = chartAddressValue ?? referralAddressValue ?? NOT_AVAILABLE;
      const t44 = referral.receivingProvider?.npiNumber || NOT_AVAILABLE;
      const t45 = referral.referringProvider?.npiNumber || NOT_AVAILABLE;
      const t46 = referral.referringLocation?.name || NOT_AVAILABLE;
      let t47;
      if ($[117] !== referral.primaryCoverage) {
        t47 = referral.primaryCoverage ? getCoverageDisplayName({
          coverage: referral.primaryCoverage,
          includesPlan: true,
          includeSubscriberName: false
        }) : "Self Pay";
        $[117] = referral.primaryCoverage;
        $[118] = t47;
      } else {
        t47 = $[118];
      }
      const t48 = referral.primaryCoverage?.subscriberId || NOT_AVAILABLE;
      const t49 = referral.primaryCoverage?.payer;
      let t50;
      if ($[119] !== t49) {
        t50 = isTprSpecialCaseInsurance(t49);
        $[119] = t49;
        $[120] = t50;
      } else {
        t50 = $[120];
      }
      let t51;
      if ($[121] !== dateOfReferral || $[122] !== diagnosis || $[123] !== diagnosisCategories || $[124] !== emergencyContactName || $[125] !== emergencyContactPhoneNumber || $[126] !== emergencyContactRelationship || $[127] !== receivingProviderName || $[128] !== referringLocationAddress || $[129] !== referringProviderName || $[130] !== referringProviderSpecialty || $[131] !== t36 || $[132] !== t37 || $[133] !== t38 || $[134] !== t39 || $[135] !== t40 || $[136] !== t41 || $[137] !== t42 || $[138] !== t43 || $[139] !== t44 || $[140] !== t45 || $[141] !== t46 || $[142] !== t47 || $[143] !== t48 || $[144] !== t50) {
        t51 = {
          patientName: t36,
          patientDateOfBirth: t37,
          patientPhoneNumber: t38,
          patientFaxNumber: t39,
          patientEthnicity: t40,
          patientRace: t41,
          patientLanguage: t42,
          patientHomeAddress: t43,
          receivingProviderName,
          receivingProviderNpi: t44,
          referringProviderName,
          referringProviderNpi: t45,
          referringProviderSpecialty,
          referringLocationName: t46,
          referringLocationAddress,
          emergencyContactName,
          emergencyContactPhoneNumber,
          emergencyContactRelationship,
          referralDate: dateOfReferral,
          primaryPlanLabel: t47,
          primarySubscriberId: t48,
          isSpecialCaseInsurance: t50,
          diagnosis,
          diagnosisCategories
        };
        $[121] = dateOfReferral;
        $[122] = diagnosis;
        $[123] = diagnosisCategories;
        $[124] = emergencyContactName;
        $[125] = emergencyContactPhoneNumber;
        $[126] = emergencyContactRelationship;
        $[127] = receivingProviderName;
        $[128] = referringLocationAddress;
        $[129] = referringProviderName;
        $[130] = referringProviderSpecialty;
        $[131] = t36;
        $[132] = t37;
        $[133] = t38;
        $[134] = t39;
        $[135] = t40;
        $[136] = t41;
        $[137] = t42;
        $[138] = t43;
        $[139] = t44;
        $[140] = t45;
        $[141] = t46;
        $[142] = t47;
        $[143] = t48;
        $[144] = t50;
        $[145] = t51;
      } else {
        t51 = $[145];
      }
      const opsCrossCheck = t51;
      if (view === "steps") {
        let t52;
        if ($[146] !== actionNote || $[147] !== bucketLabel || $[148] !== chartReferralAuths || $[149] !== chartRequestLetters || $[150] !== coverageReadback || $[151] !== decisionTrace || $[152] !== documentId || $[153] !== documentUploadLabels || $[154] !== followThroughEligible || $[155] !== hasMissingInformation || $[156] !== insuranceRequirements || $[157] !== insuranceStatusDisplay || $[158] !== internalNoteToEnter || $[159] !== isPrimaryCareProvider || $[160] !== opsCrossCheck || $[161] !== pastAppointments || $[162] !== patient.displayId || $[163] !== patientEhrId || $[164] !== patientLabel || $[165] !== pendingApproval || $[166] !== primaryCareProviderAlreadyPresent || $[167] !== privacyNoticeOnFile || $[168] !== receivingAssignmentConfirmed || $[169] !== referral.id || $[170] !== referralAuthorization || $[171] !== resolvedReceiving || $[172] !== task || $[173] !== taskSteps || $[174] !== unresolvedUpcomingProviders || $[175] !== upcomingAppointmentProviderSummary || $[176] !== upcomingAppointments || $[177] !== urgency) {
          t52 = <div className="flex w-full max-w-2xl flex-col gap-8 sm:min-w-[800px] mx-auto"><TprSyncTimeline task={task} referralId={referral.id} taskSteps={taskSteps} pendingApproval={pendingApproval} receivingAssignmentConfirmed={receivingAssignmentConfirmed} followThroughEligible={followThroughEligible} coverageReadback={coverageReadback} insuranceRequirements={insuranceRequirements} referralAuthorization={referralAuthorization} resolvedReceiving={resolvedReceiving} patientLabel={patientLabel} patientEhrId={patientEhrId} documentId={documentId} bucketLabel={bucketLabel} internalNoteToEnter={internalNoteToEnter} urgency={urgency} actionNote={actionNote} decisionTrace={decisionTrace} insuranceStatusDisplay={insuranceStatusDisplay} hasMissingInformation={hasMissingInformation} unresolvedUpcomingProviders={unresolvedUpcomingProviders} upcomingAppointmentProviderSummary={upcomingAppointmentProviderSummary} upcomingAppointments={upcomingAppointments} pastAppointments={pastAppointments} documentUploadLabels={documentUploadLabels} privacyNoticeOnFile={privacyNoticeOnFile} isPrimaryCareProvider={isPrimaryCareProvider} primaryCareProviderAlreadyPresent={primaryCareProviderAlreadyPresent} patientDisplayId={patient.displayId} opsCrossCheck={opsCrossCheck} chartReferralAuths={chartReferralAuths} chartRequestLetters={chartRequestLetters} /></div>;
          $[146] = actionNote;
          $[147] = bucketLabel;
          $[148] = chartReferralAuths;
          $[149] = chartRequestLetters;
          $[150] = coverageReadback;
          $[151] = decisionTrace;
          $[152] = documentId;
          $[153] = documentUploadLabels;
          $[154] = followThroughEligible;
          $[155] = hasMissingInformation;
          $[156] = insuranceRequirements;
          $[157] = insuranceStatusDisplay;
          $[158] = internalNoteToEnter;
          $[159] = isPrimaryCareProvider;
          $[160] = opsCrossCheck;
          $[161] = pastAppointments;
          $[162] = patient.displayId;
          $[163] = patientEhrId;
          $[164] = patientLabel;
          $[165] = pendingApproval;
          $[166] = primaryCareProviderAlreadyPresent;
          $[167] = privacyNoticeOnFile;
          $[168] = receivingAssignmentConfirmed;
          $[169] = referral.id;
          $[170] = referralAuthorization;
          $[171] = resolvedReceiving;
          $[172] = task;
          $[173] = taskSteps;
          $[174] = unresolvedUpcomingProviders;
          $[175] = upcomingAppointmentProviderSummary;
          $[176] = upcomingAppointments;
          $[177] = urgency;
          $[178] = t52;
        } else {
          t52 = $[178];
        }
        t25 = t52;
        break bb0;
      }
      t24 = "flex flex-col gap-8 sm:min-w-[800px] w-full max-w-2xl mx-auto";
      T0 = EhrInstructionsCard;
      t23 = "Detailed Athena verification reference";
      t16 = "text-sm text-muted-foreground space-y-6";
      if ($[179] === Symbol.for("react.memo_cache_sentinel")) {
        t17 = <h3 className="text-md font-semibold text-center text-foreground">TPR Biltmore Cardiology Sync Verification</h3>;
        $[179] = t17;
      } else {
        t17 = $[179];
      }
      const t52 = syncFailed ? "Automation did not complete for this task. Resolve the sync error below and verify Athena before closing." : "Automation has already run for this task. Use this checklist to verify the sync in Athena before closing.";
      if ($[180] !== t52) {
        t18 = <p className="text-xs text-center">{t52}</p>;
        $[180] = t52;
        $[181] = t18;
      } else {
        t18 = $[181];
      }
      if ($[182] !== task.id) {
        t19 = <SyncIncompleteActions taskId={task.id} />;
        $[182] = task.id;
        $[183] = t19;
      } else {
        t19 = $[183];
      }
      let t53;
      if ($[184] === Symbol.for("react.memo_cache_sentinel")) {
        t53 = <strong>Camelback Referral:</strong>;
        $[184] = t53;
      } else {
        t53 = $[184];
      }
      const t54 = isCamelbackReferral ? "Yes" : "No";
      let t55;
      if ($[185] !== t54) {
        t55 = <p>{t53} {t54}</p>;
        $[185] = t54;
        $[186] = t55;
      } else {
        t55 = $[186];
      }
      let t56;
      if ($[187] === Symbol.for("react.memo_cache_sentinel")) {
        t56 = <strong>Patient:</strong>;
        $[187] = t56;
      } else {
        t56 = $[187];
      }
      let t57;
      if ($[188] !== patientDisplayName) {
        t57 = <CopyableContent text={patientDisplayName} />;
        $[188] = patientDisplayName;
        $[189] = t57;
      } else {
        t57 = $[189];
      }
      let t58;
      if ($[190] !== patientLabel || $[191] !== t57) {
        t58 = <p>{t56} {t57} ({patientLabel})</p>;
        $[190] = patientLabel;
        $[191] = t57;
        $[192] = t58;
      } else {
        t58 = $[192];
      }
      let t59;
      if ($[193] === Symbol.for("react.memo_cache_sentinel")) {
        t59 = <strong>Receiving Provider (as extracted):</strong>;
        $[193] = t59;
      } else {
        t59 = $[193];
      }
      let t60;
      if ($[194] !== referral.receivingProvider) {
        t60 = <ProviderCopyFields provider={referral.receivingProvider} />;
        $[194] = referral.receivingProvider;
        $[195] = t60;
      } else {
        t60 = $[195];
      }
      let t61;
      if ($[196] === Symbol.for("react.memo_cache_sentinel")) {
        t61 = <strong>Location:</strong>;
        $[196] = t61;
      } else {
        t61 = $[196];
      }
      let t62;
      if ($[197] !== receivingLocationName) {
        t62 = <CopyableContent text={receivingLocationName} />;
        $[197] = receivingLocationName;
        $[198] = t62;
      } else {
        t62 = $[198];
      }
      let t63;
      if ($[199] !== t60 || $[200] !== t62) {
        t63 = <p>{t59}{" "}{t60}, {t61}{" "}{t62}</p>;
        $[199] = t60;
        $[200] = t62;
        $[201] = t63;
      } else {
        t63 = $[201];
      }
      if ($[202] !== t55 || $[203] !== t58 || $[204] !== t63) {
        t20 = <div className="rounded-md border border-foreground/20 p-3 space-y-1 text-foreground">{t55}{t58}{t63}</div>;
        $[202] = t55;
        $[203] = t58;
        $[204] = t63;
        $[205] = t20;
      } else {
        t20 = $[205];
      }
      let t64;
      let t65;
      if ($[206] === Symbol.for("react.memo_cache_sentinel")) {
        t64 = <p className="font-semibold">Recommended Receiving Provider / Location</p>;
        t65 = <strong>Provider:</strong>;
        $[206] = t64;
        $[207] = t65;
      } else {
        t64 = $[206];
        t65 = $[207];
      }
      const t66 = resolvedReceiving.providerLabel || NOT_AVAILABLE;
      let t67;
      if ($[208] !== t66) {
        t67 = <CopyableContent text={t66} />;
        $[208] = t66;
        $[209] = t67;
      } else {
        t67 = $[209];
      }
      let t68;
      if ($[210] === Symbol.for("react.memo_cache_sentinel")) {
        t68 = <strong>Location:</strong>;
        $[210] = t68;
      } else {
        t68 = $[210];
      }
      const t69 = resolvedReceiving.locationLabel || NOT_AVAILABLE;
      let t70;
      if ($[211] !== t69) {
        t70 = <CopyableContent text={t69} />;
        $[211] = t69;
        $[212] = t70;
      } else {
        t70 = $[212];
      }
      let t71;
      if ($[213] !== t67 || $[214] !== t70) {
        t71 = <p>{t65} {t67},{" "}{t68} {t70}</p>;
        $[213] = t67;
        $[214] = t70;
        $[215] = t71;
      } else {
        t71 = $[215];
      }
      let t72;
      if ($[216] !== resolvedReceiving.routedToSscardio) {
        t72 = resolvedReceiving.routedToSscardio && <p className="text-xs">Routing: <strong>sscardio</strong></p>;
        $[216] = resolvedReceiving.routedToSscardio;
        $[217] = t72;
      } else {
        t72 = $[217];
      }
      let t73;
      if ($[218] !== resolvedReceiving.flags) {
        t73 = resolvedReceiving.flags.length > 0 && <ul className="ml-4 mt-1 space-y-1 list-disc list-inside text-xs">{resolvedReceiving.flags.map(_temp32)}</ul>;
        $[218] = resolvedReceiving.flags;
        $[219] = t73;
      } else {
        t73 = $[219];
      }
      let t74;
      if ($[220] !== resolvedReceiving.trace) {
        t74 = resolvedReceiving.trace.length > 0 && <details className="text-xs"><summary>Why</summary><ul className="ml-4 mt-1 space-y-1 list-disc list-inside">{resolvedReceiving.trace.map(_temp33)}</ul></details>;
        $[220] = resolvedReceiving.trace;
        $[221] = t74;
      } else {
        t74 = $[221];
      }
      if ($[222] !== t71 || $[223] !== t72 || $[224] !== t73 || $[225] !== t74) {
        t21 = <div className="rounded-md border border-foreground/20 p-3 space-y-1 text-foreground">{t64}{t71}{t72}{t73}{t74}</div>;
        $[222] = t71;
        $[223] = t72;
        $[224] = t73;
        $[225] = t74;
        $[226] = t21;
      } else {
        t21 = $[226];
      }
      let t75;
      if ($[227] === Symbol.for("react.memo_cache_sentinel")) {
        t75 = <h4 className="font-semibold text-foreground mb-2">Patient Chart Create</h4>;
        $[227] = t75;
      } else {
        t75 = $[227];
      }
      let t76;
      if ($[228] !== documentId) {
        t76 = <li>Search by document ID: <CopyableContent text={documentId} /></li>;
        $[228] = documentId;
        $[229] = t76;
      } else {
        t76 = $[229];
      }
      let t77;
      if ($[230] !== patientEhrId) {
        t77 = <li>Add patient EHR ID if not already added: <CopyableContent text={patientEhrId} /></li>;
        $[230] = patientEhrId;
        $[231] = t77;
      } else {
        t77 = $[231];
      }
      let t78;
      if ($[232] === Symbol.for("react.memo_cache_sentinel")) {
        t78 = <li>Hit save and proceed to quickview.</li>;
        $[232] = t78;
      } else {
        t78 = $[232];
      }
      let t79;
      if ($[233] !== resolvedReceiving.providerLabel || $[234] !== resolvedReceiving.providerNpi || $[235] !== resolvedReceiving.usualProviderWriteSafe) {
        t79 = resolvedReceiving.usualProviderWriteSafe ? <ResolvedProviderCopyFields providerLabel={resolvedReceiving.providerLabel} providerNpi={resolvedReceiving.providerNpi} writeSafe={true} /> : "Leave unchanged";
        $[233] = resolvedReceiving.providerLabel;
        $[234] = resolvedReceiving.providerNpi;
        $[235] = resolvedReceiving.usualProviderWriteSafe;
        $[236] = t79;
      } else {
        t79 = $[236];
      }
      const t80 = resolvedReceiving.usualProviderWriteSafe ? "New patient \u2014 add the usual provider to the care team." : "Established patient \u2014 left unchanged to preserve continuity of care.";
      let t81;
      if ($[237] !== t80) {
        t81 = <p className="text-xs italic mt-1">{t80}</p>;
        $[237] = t80;
        $[238] = t81;
      } else {
        t81 = $[238];
      }
      let t82;
      if ($[239] !== t79 || $[240] !== t81) {
        t82 = <li>Usual Provider:{" "}{t79}{t81}</li>;
        $[239] = t79;
        $[240] = t81;
        $[241] = t82;
      } else {
        t82 = $[241];
      }
      let t83;
      if ($[242] !== referral.referringProvider) {
        t83 = <ProviderCopyFields provider={referral.referringProvider} />;
        $[242] = referral.referringProvider;
        $[243] = t83;
      } else {
        t83 = $[243];
      }
      let t84;
      if ($[244] !== referringProviderSpecialty) {
        t84 = <CopyableContent text={referringProviderSpecialty} />;
        $[244] = referringProviderSpecialty;
        $[245] = t84;
      } else {
        t84 = $[245];
      }
      let t85;
      if ($[246] !== t83 || $[247] !== t84) {
        t85 = <li>Referring Provider: {t83}, Specialty:{" "}{t84}</li>;
        $[246] = t83;
        $[247] = t84;
        $[248] = t85;
      } else {
        t85 = $[248];
      }
      let t86;
      if ($[249] !== isPrimaryCareProvider || $[250] !== referral.referringProvider) {
        t86 = isPrimaryCareProvider && <li>PCP: <ProviderCopyFields provider={referral.referringProvider} /></li>;
        $[249] = isPrimaryCareProvider;
        $[250] = referral.referringProvider;
        $[251] = t86;
      } else {
        t86 = $[251];
      }
      let t87;
      if ($[252] !== referral.referringProvider || $[253] !== referringLocationAddress) {
        t87 = referral.referringProvider && <li>Referring provider address: <CopyableContent text={referringLocationAddress} /></li>;
        $[252] = referral.referringProvider;
        $[253] = referringLocationAddress;
        $[254] = t87;
      } else {
        t87 = $[254];
      }
      let t88;
      if ($[255] !== t82 || $[256] !== t85 || $[257] !== t86 || $[258] !== t87) {
        t88 = <li>Check the Patient Care Team:<ul className="ml-4 mt-1 space-y-1 list-disc list-inside">{t82}{t85}{t86}{t87}</ul></li>;
        $[255] = t82;
        $[256] = t85;
        $[257] = t86;
        $[258] = t87;
        $[259] = t88;
      } else {
        t88 = $[259];
      }
      let t89;
      if ($[260] !== emergencyContactName) {
        t89 = <li>Name: <CopyableContent text={emergencyContactName} /></li>;
        $[260] = emergencyContactName;
        $[261] = t89;
      } else {
        t89 = $[261];
      }
      let t90;
      if ($[262] !== emergencyContactPhoneNumber) {
        t90 = <li>Phone Number: <CopyableContent text={emergencyContactPhoneNumber} /></li>;
        $[262] = emergencyContactPhoneNumber;
        $[263] = t90;
      } else {
        t90 = $[263];
      }
      let t91;
      if ($[264] !== emergencyContactRelationship) {
        t91 = <li>Relationship to Patient: <CopyableContent text={emergencyContactRelationship} /></li>;
        $[264] = emergencyContactRelationship;
        $[265] = t91;
      } else {
        t91 = $[265];
      }
      let t92;
      if ($[266] !== t89 || $[267] !== t90 || $[268] !== t91) {
        t92 = <li>Check the Emergency Contact<ul className="ml-4 mt-1 space-y-1 list-disc list-inside">{t89}{t90}{t91}</ul></li>;
        $[266] = t89;
        $[267] = t90;
        $[268] = t91;
        $[269] = t92;
      } else {
        t92 = $[269];
      }
      const t93 = primaryCoverageSummary ?? "N/A";
      let t94;
      if ($[270] !== t93) {
        t94 = <li>Primary: <CopyableContent text={t93} /></li>;
        $[270] = t93;
        $[271] = t94;
      } else {
        t94 = $[271];
      }
      let t95;
      if ($[272] !== secondaryCoverageSummary) {
        t95 = secondaryCoverageSummary && <li>Secondary: <CopyableContent text={secondaryCoverageSummary} /></li>;
        $[272] = secondaryCoverageSummary;
        $[273] = t95;
      } else {
        t95 = $[273];
      }
      let t96;
      let t97;
      if ($[274] !== insuranceStatusDisplay) {
        t96 = <CopyableContent text={insuranceStatusDisplay} />;
        t97 = insuranceStatusDisplay === "Missing Insurance" && <p className="text-xs italic mt-1">Ops needs to call the referring provider to verify insurance.</p>;
        $[274] = insuranceStatusDisplay;
        $[275] = t96;
        $[276] = t97;
      } else {
        t96 = $[275];
        t97 = $[276];
      }
      let t98;
      if ($[277] !== insuranceStatusDisplay) {
        t98 = insuranceStatusDisplay === "Unverified" && <p className="text-xs italic mt-1">Ops needs to call the referring provider to verify insurance.</p>;
        $[277] = insuranceStatusDisplay;
        $[278] = t98;
      } else {
        t98 = $[278];
      }
      let t99;
      if ($[279] !== t96 || $[280] !== t97 || $[281] !== t98) {
        t99 = <li>Eligibility check result: {t96}{t97}{t98}</li>;
        $[279] = t96;
        $[280] = t97;
        $[281] = t98;
        $[282] = t99;
      } else {
        t99 = $[282];
      }
      let t100;
      if ($[283] !== t94 || $[284] !== t95 || $[285] !== t99) {
        t100 = <li>Check the patient insurance<ul className="ml-4 mt-1 space-y-1 list-disc list-inside">{t94}{t95}{t99}</ul></li>;
        $[283] = t94;
        $[284] = t95;
        $[285] = t99;
        $[286] = t100;
      } else {
        t100 = $[286];
      }
      let t101;
      if ($[287] === Symbol.for("react.memo_cache_sentinel")) {
        t101 = <li>Call and text are YES</li>;
        $[287] = t101;
      } else {
        t101 = $[287];
      }
      let t102;
      if ($[288] !== privacyNoticeOnFile) {
        t102 = privacyNoticeOnFile !== true && <p className="text-xs italic mt-1">{privacyNoticeOnFile === false ? "Privacy is not set on the Athena chart. Set it to YES during review." : "Could not verify Athena privacy. Confirm it is YES during review."}</p>;
        $[288] = privacyNoticeOnFile;
        $[289] = t102;
      } else {
        t102 = $[289];
      }
      let t103;
      if ($[290] !== t102) {
        t103 = <li>Privacy is YES{t102}</li>;
        $[290] = t102;
        $[291] = t103;
      } else {
        t103 = $[291];
      }
      let t104;
      if ($[292] === Symbol.for("react.memo_cache_sentinel")) {
        t104 = <li>If How did you hear about us is blank, set it to PHYSICIAN REFERRAL; leave any existing value unchanged.</li>;
        $[292] = t104;
      } else {
        t104 = $[292];
      }
      let t105;
      if ($[293] !== t103) {
        t105 = <li>Go to full registration and verify:<ul className="ml-4 mt-1 space-y-1 list-disc list-inside">{t101}{t103}{t104}</ul></li>;
        $[293] = t103;
        $[294] = t105;
      } else {
        t105 = $[294];
      }
      if ($[295] !== t100 || $[296] !== t105 || $[297] !== t76 || $[298] !== t77 || $[299] !== t88 || $[300] !== t92) {
        t22 = <div>{t75}<ol className="list-decimal list-inside space-y-3">{t76}{t77}{t78}{t88}{t92}{t100}{t105}</ol></div>;
        $[295] = t100;
        $[296] = t105;
        $[297] = t76;
        $[298] = t77;
        $[299] = t88;
        $[300] = t92;
        $[301] = t22;
      } else {
        t22 = $[301];
      }
      if ($[302] === Symbol.for("react.memo_cache_sentinel")) {
        t15 = <h4 className="font-semibold text-foreground mb-2">Referral Processing</h4>;
        $[302] = t15;
      } else {
        t15 = $[302];
      }
      t11 = "list-decimal list-inside space-y-3";
      if ($[303] !== documentId) {
        t12 = <li>Search by document ID: <CopyableContent text={documentId} /></li>;
        $[303] = documentId;
        $[304] = t12;
      } else {
        t12 = $[304];
      }
      let t106;
      if ($[305] !== referringProviderName) {
        t106 = <CopyableContent text={referringProviderName} />;
        $[305] = referringProviderName;
        $[306] = t106;
      } else {
        t106 = $[306];
      }
      const t107 = referral.referringProvider?.npiNumber || NOT_AVAILABLE;
      let t108;
      if ($[307] !== t107) {
        t108 = <CopyableContent text={t107} />;
        $[307] = t107;
        $[308] = t108;
      } else {
        t108 = $[308];
      }
      let t109;
      if ($[309] !== referringLocationAddress) {
        t109 = <CopyableContent text={referringLocationAddress} />;
        $[309] = referringLocationAddress;
        $[310] = t109;
      } else {
        t109 = $[310];
      }
      if ($[311] !== t106 || $[312] !== t108 || $[313] !== t109) {
        t13 = <li>Referring provider: Name: {t106}, NPI:{" "}{t108}, Address:{" "}{t109}</li>;
        $[311] = t106;
        $[312] = t108;
        $[313] = t109;
        $[314] = t13;
      } else {
        t13 = $[314];
      }
      if ($[315] !== resolvedReceiving.providerLabel || $[316] !== resolvedReceiving.providerNpi || $[317] !== resolvedReceiving.providerWriteSafe) {
        t14 = <li>Receiving provider:{" "}<ResolvedProviderCopyFields providerLabel={resolvedReceiving.providerLabel} providerNpi={resolvedReceiving.providerNpi} writeSafe={resolvedReceiving.providerWriteSafe} /></li>;
        $[315] = resolvedReceiving.providerLabel;
        $[316] = resolvedReceiving.providerNpi;
        $[317] = resolvedReceiving.providerWriteSafe;
        $[318] = t14;
      } else {
        t14 = $[318];
      }
      t9 = "Patient Type: ";
      if ($[319] !== patientLabel) {
        t10 = <CopyableContent text={patientLabel} />;
        $[319] = patientLabel;
        $[320] = t10;
      } else {
        t10 = $[320];
      }
      t7 = "ml-4 mt-1 space-y-1 list-disc list-inside text-xs";
      t8 = sortedPastAppointments.length === 0 ? <li>No appointment history found</li> : sortedPastAppointments.map(_temp34);
    }
    $[12] = actionNote;
    $[13] = athenaData?.coverages;
    $[14] = athenaData?.patientDetails;
    $[15] = bucketLabel;
    $[16] = chartReferralAuths;
    $[17] = chartRequestLetters;
    $[18] = coverageReadback;
    $[19] = dateOfReferral;
    $[20] = decisionTrace;
    $[21] = documentId;
    $[22] = documentUploadLabels;
    $[23] = eligibilityDisplay;
    $[24] = emergencyContactName;
    $[25] = emergencyContactPhoneNumber;
    $[26] = emergencyContactRelationship;
    $[27] = followThroughEligible;
    $[28] = insuranceRequirements;
    $[29] = internalNoteToEnter;
    $[30] = isPrimaryCareProvider;
    $[31] = pastAppointments;
    $[32] = patient;
    $[33] = patientEhrId;
    $[34] = patientLabel;
    $[35] = pendingApproval;
    $[36] = primaryCareProviderAlreadyPresent;
    $[37] = primaryCoverageSummary;
    $[38] = privacyNoticeOnFile;
    $[39] = receivingAssignmentConfirmed;
    $[40] = receivingProviderName;
    $[41] = referral;
    $[42] = referralAuthorization;
    $[43] = referringLocationAddress;
    $[44] = referringProviderName;
    $[45] = referringProviderSpecialty;
    $[46] = resolvedReceiving;
    $[47] = secondaryCoverageSummary;
    $[48] = task;
    $[49] = taskSteps;
    $[50] = unresolvedUpcomingProviders;
    $[51] = upcomingAppointmentProviderSummary;
    $[52] = upcomingAppointments;
    $[53] = urgency;
    $[54] = view;
    $[55] = T0;
    $[56] = dataSourceEntries;
    $[57] = hasMissingInformation;
    $[58] = missingFields;
    $[59] = t10;
    $[60] = t11;
    $[61] = t12;
    $[62] = t13;
    $[63] = t14;
    $[64] = t15;
    $[65] = t16;
    $[66] = t17;
    $[67] = t18;
    $[68] = t19;
    $[69] = t20;
    $[70] = t21;
    $[71] = t22;
    $[72] = t23;
    $[73] = t24;
    $[74] = t25;
    $[75] = t7;
    $[76] = t8;
    $[77] = t9;
  } else {
    T0 = $[55];
    dataSourceEntries = $[56];
    hasMissingInformation = $[57];
    missingFields = $[58];
    t10 = $[59];
    t11 = $[60];
    t12 = $[61];
    t13 = $[62];
    t14 = $[63];
    t15 = $[64];
    t16 = $[65];
    t17 = $[66];
    t18 = $[67];
    t19 = $[68];
    t20 = $[69];
    t21 = $[70];
    t22 = $[71];
    t23 = $[72];
    t24 = $[73];
    t25 = $[74];
    t7 = $[75];
    t8 = $[76];
    t9 = $[77];
  }
  if (t25 !== Symbol.for("react.early_return_sentinel")) {
    return t25;
  }
  let t26;
  if ($[321] !== t7 || $[322] !== t8) {
    t26 = <ul className={t7}>{t8}</ul>;
    $[321] = t7;
    $[322] = t8;
    $[323] = t26;
  } else {
    t26 = $[323];
  }
  let t27;
  if ($[324] !== t10 || $[325] !== t26 || $[326] !== t9) {
    t27 = <li>{t9}{t10}{t26}</li>;
    $[324] = t10;
    $[325] = t26;
    $[326] = t9;
    $[327] = t27;
  } else {
    t27 = $[327];
  }
  let t28;
  if ($[328] !== dateOfReferral) {
    t28 = <li>Date: <CopyableContent text={dateOfReferral} /></li>;
    $[328] = dateOfReferral;
    $[329] = t28;
  } else {
    t28 = $[329];
  }
  let t29;
  if ($[330] !== internalNoteToEnter) {
    t29 = <CopyableContent text={internalNoteToEnter} />;
    $[330] = internalNoteToEnter;
    $[331] = t29;
  } else {
    t29 = $[331];
  }
  let t30;
  if ($[332] !== flags) {
    t30 = flags.length > 0 && <ul className="ml-4 mt-1 space-y-1 list-disc list-inside text-xs">{flags.map(_temp35)}</ul>;
    $[332] = flags;
    $[333] = t30;
  } else {
    t30 = $[333];
  }
  let t31;
  if ($[334] !== t29 || $[335] !== t30) {
    t31 = <li>Internal Note: {t29}{t30}</li>;
    $[334] = t29;
    $[335] = t30;
    $[336] = t31;
  } else {
    t31 = $[336];
  }
  let t32;
  if ($[337] !== schedulingPriority) {
    t32 = <li>Urgency: <CopyableContent text={schedulingPriority} /></li>;
    $[337] = schedulingPriority;
    $[338] = t32;
  } else {
    t32 = $[338];
  }
  let t33;
  if ($[339] !== bucketLabel) {
    t33 = bucketLabel === TPR_MANUAL_REVIEW_BUCKET ? <>{NO_CONFIDENT_BUCKET_SUGGESTION}</> : <>Suggested Bucket: <CopyableContent text={SUGGESTED_BUCKET_LABELS[bucketLabel] ?? bucketLabel} /></>;
    $[339] = bucketLabel;
    $[340] = t33;
  } else {
    t33 = $[340];
  }
  let t34;
  if ($[341] !== bucketLabel || $[342] !== decisionTrace) {
    t34 = decisionTrace.length > 0 && (bucketLabel === TPR_MANUAL_REVIEW_BUCKET ? <ul className="ml-4 mt-1 space-y-1 list-disc list-inside text-xs">{decisionTrace.map(_temp36)}</ul> : <ul className="ml-4 mt-1 space-y-1 list-disc list-inside text-xs"><li>{decisionTrace[decisionTrace.length - 1].label}{" "}<strong>{decisionTrace[decisionTrace.length - 1].outcome}</strong></li></ul>);
    $[341] = bucketLabel;
    $[342] = decisionTrace;
    $[343] = t34;
  } else {
    t34 = $[343];
  }
  let t35;
  if ($[344] !== t33 || $[345] !== t34) {
    t35 = <li>{t33}{t34}</li>;
    $[344] = t33;
    $[345] = t34;
    $[346] = t35;
  } else {
    t35 = $[346];
  }
  let t36;
  if ($[347] !== actionNote) {
    t36 = <li>Action note: <CopyableContent text={actionNote} /></li>;
    $[347] = actionNote;
    $[348] = t36;
  } else {
    t36 = $[348];
  }
  let t37;
  if ($[349] !== internalNoteToEnter) {
    t37 = <li>Go to quickview and add the internal note: <CopyableContent text={internalNoteToEnter} /></li>;
    $[349] = internalNoteToEnter;
    $[350] = t37;
  } else {
    t37 = $[350];
  }
  let t38;
  if ($[351] !== t11 || $[352] !== t12 || $[353] !== t13 || $[354] !== t14 || $[355] !== t27 || $[356] !== t28 || $[357] !== t31 || $[358] !== t32 || $[359] !== t35 || $[360] !== t36 || $[361] !== t37) {
    t38 = <ol className={t11}>{t12}{t13}{t14}{t27}{t28}{t31}{t32}{t35}{t36}{t37}</ol>;
    $[351] = t11;
    $[352] = t12;
    $[353] = t13;
    $[354] = t14;
    $[355] = t27;
    $[356] = t28;
    $[357] = t31;
    $[358] = t32;
    $[359] = t35;
    $[360] = t36;
    $[361] = t37;
    $[362] = t38;
  } else {
    t38 = $[362];
  }
  let t39;
  if ($[363] !== t15 || $[364] !== t38) {
    t39 = <div>{t15}{t38}</div>;
    $[363] = t15;
    $[364] = t38;
    $[365] = t39;
  } else {
    t39 = $[365];
  }
  let t40;
  let t41;
  if ($[366] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <h4 className="font-semibold text-foreground mb-2">Doc Upload</h4>;
    t41 = <p className="mb-1">Verify the following documents were uploaded:</p>;
    $[366] = t40;
    $[367] = t41;
  } else {
    t40 = $[366];
    t41 = $[367];
  }
  let t42;
  if ($[368] !== documentUploadLabels) {
    t42 = documentUploadLabels.length === 0 ? <li>N/A</li> : documentUploadLabels.map(_temp37);
    $[368] = documentUploadLabels;
    $[369] = t42;
  } else {
    t42 = $[369];
  }
  let t43;
  if ($[370] !== t42) {
    t43 = <div>{t40}{t41}<ul className="ml-4 space-y-1 list-disc list-inside">{t42}</ul></div>;
    $[370] = t42;
    $[371] = t43;
  } else {
    t43 = $[371];
  }
  let t44;
  let t45;
  if ($[372] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <h4 className="font-semibold text-foreground mb-2">Missing Information</h4>;
    t45 = <p className="font-medium text-foreground">Step 1: Missing Information Check</p>;
    $[372] = t44;
    $[373] = t45;
  } else {
    t44 = $[372];
    t45 = $[373];
  }
  let t46;
  if ($[374] !== hasMissingInformation || $[375] !== missingFields) {
    t46 = hasMissingInformation ? `Missing: ${missingFields.map(_temp38).join(", ")}` : "No missing-information letter needed.";
    $[374] = hasMissingInformation;
    $[375] = missingFields;
    $[376] = t46;
  } else {
    t46 = $[376];
  }
  let t47;
  if ($[377] !== t46) {
    t47 = <p className="mb-3">{t46}</p>;
    $[377] = t46;
    $[378] = t47;
  } else {
    t47 = $[378];
  }
  let t48;
  if ($[379] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = <p className="font-medium text-foreground">Step 2: Data Source Checklist</p>;
    $[379] = t48;
  } else {
    t48 = $[379];
  }
  let t49;
  if ($[380] !== dataSourceEntries) {
    t49 = <ul className="ml-4 mb-3 space-y-1 list-disc list-inside">{dataSourceEntries.map(_temp39)}</ul>;
    $[380] = dataSourceEntries;
    $[381] = t49;
  } else {
    t49 = $[381];
  }
  let t50;
  if ($[382] !== dateOfReferral || $[383] !== hasMissingInformation || $[384] !== referringProviderName) {
    t50 = hasMissingInformation && <ol className="list-decimal list-inside space-y-2"><li>Verify the Auto-Generated document was added to the patient chart</li><li>Send the document out.<ul className="ml-4 mt-1 space-y-2 list-none"><li>a. Chart Export - sending to referring provider<ul className="ml-4 mt-1 space-y-1 list-disc list-inside"><li>Click the hamburger menu (3 dashes) → select Chart Export.</li><li>Click the + button next to Admin Documents.</li><li>Find the "Request for Additional Information" doc and move it to the right-hand side.</li><li>Click Export Chart Sections → send to <CopyableContent text={referringProviderName} /></li></ul></li><li>c. Fill out export details<ul className="ml-4 mt-1 space-y-1 list-disc list-inside"><li>Type <CopyableContent text={referringProviderName} /></li><li>Confirm method is set to Athena Fax and the fax number has populated.</li><li>Attention box: <CopyableContent text={referringProviderName} /></li><li>Note field: "Request for missing information."</li><li>Confirm Submit, then Save.</li></ul></li><li>d. Add Note to Patient Chart<ul className="ml-4 mt-1 space-y-1 list-disc list-inside"><li>"Valerie Health: Missing Information Requested <CopyableContent text={dateOfReferral} />"</li></ul></li></ul></li></ol>;
    $[382] = dateOfReferral;
    $[383] = hasMissingInformation;
    $[384] = referringProviderName;
    $[385] = t50;
  } else {
    t50 = $[385];
  }
  let t51;
  if ($[386] !== t47 || $[387] !== t49 || $[388] !== t50) {
    t51 = <div>{t44}{t45}{t47}{t48}{t49}{t50}</div>;
    $[386] = t47;
    $[387] = t49;
    $[388] = t50;
    $[389] = t51;
  } else {
    t51 = $[389];
  }
  let t52;
  if ($[390] !== t16 || $[391] !== t17 || $[392] !== t18 || $[393] !== t19 || $[394] !== t20 || $[395] !== t21 || $[396] !== t22 || $[397] !== t39 || $[398] !== t43 || $[399] !== t51) {
    t52 = <div className={t16}>{t17}{t18}{t19}{t20}{t21}{t22}{t39}{t43}{t51}</div>;
    $[390] = t16;
    $[391] = t17;
    $[392] = t18;
    $[393] = t19;
    $[394] = t20;
    $[395] = t21;
    $[396] = t22;
    $[397] = t39;
    $[398] = t43;
    $[399] = t51;
    $[400] = t52;
  } else {
    t52 = $[400];
  }
  let t53;
  if ($[401] !== T0 || $[402] !== t23 || $[403] !== t52) {
    t53 = <T0 collapsibleTitle={t23}>{t52}</T0>;
    $[401] = T0;
    $[402] = t23;
    $[403] = t52;
    $[404] = t53;
  } else {
    t53 = $[404];
  }
  let t54;
  if ($[405] !== referral.id || $[406] !== task.id) {
    t54 = <ReferralEligibilityCard referralId={referral.id} taskId={task.id} />;
    $[405] = referral.id;
    $[406] = task.id;
    $[407] = t54;
  } else {
    t54 = $[407];
  }
  let t55;
  if ($[408] !== athenaData || $[409] !== referral) {
    t55 = <AthenaReferralCards referral={referral} athenaData={athenaData} />;
    $[408] = athenaData;
    $[409] = referral;
    $[410] = t55;
  } else {
    t55 = $[410];
  }
  let t56;
  if ($[411] !== t24 || $[412] !== t53 || $[413] !== t54 || $[414] !== t55) {
    t56 = <div className={t24}>{t53}{t54}{t55}</div>;
    $[411] = t24;
    $[412] = t53;
    $[413] = t54;
    $[414] = t55;
    $[415] = t56;
  } else {
    t56 = $[415];
  }
  return t56;
}
function _temp39(entry_2) {
  return <li key={entry_2.label}>{entry_2.label}: <CopyableContent text={entry_2.value} /> ({entry_2.source})</li>;
}
function _temp38(entry_1) {
  return entry_1.label;
}
function _temp37(label) {
  return <li key={label}><CopyableContent text={label} /></li>;
}
function _temp36(entry_0, index_3) {
  return <li key={`${entry_0.label}-${index_3}`}>{entry_0.label} <strong>{entry_0.outcome}</strong></li>;
}
function _temp35(flag_0, index_2) {
  return <li key={index_2}>{flag_0}</li>;
}
function _temp34(appointment) {
  return <li key={appointment.id}>{formatAppointmentHistoryEntry(appointment)}</li>;
}
function _temp33(entry, index_1) {
  return <li key={index_1}>{entry}</li>;
}
function _temp32(flag, index_0) {
  return <li key={index_0}>{flag}</li>;
}
function _temp31(value_0, index, values) {
  return values.indexOf(value_0) === index;
}
function _temp30(value) {
  return Boolean(value);
}
function _temp29(item) {
  return item.diagnosisText;
}
function _temp28(a, b) {
  return (a.order ?? 99) - (b.order ?? 99);
}